import { useState, useEffect, useRef } from "react";
import { LocationPicker } from "@/components/LocationPicker";
import { PhoneInput } from "@/components/PhoneInput";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import {
  useGetMe, useListProjects, useListClientInvoices,
  useListClientReceipts, useListCompanies, useCreateProject, useUpdateProject, useDeleteProject,
  useUpdateCompany, useListPromissoryNotes,
  useListScInvoices, useListScPayments,
  useListSupplierInvoices, useListSupplierPayments,
  useListBudget, useListPayroll,
  useListClientDocuments, useListVendorOrders,
  useListCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer,
  useListSubcontractors, useListMasterSCs, useCreateMasterSC, useUpdateMasterSC, useDeleteMasterSC,
  useListSuppliers, useListMasterSuppliers, useCreateMasterSupplier, useUpdateMasterSupplier, useDeleteMasterSupplier,
  useListMasterStaff, useCreateMasterStaffMember, useUpdateMasterStaffMember, useDeleteMasterStaffMember,
  useListExpenseCategories, useCreateExpenseCategory, useUpdateExpenseCategory, useDeleteExpenseCategory,
  getListExpenseCategoriesQueryKey,
  useListTaxTypes, useCreateTaxType, useUpdateTaxType, useDeleteTaxType,
  useCreateTaxRate, useUpdateTaxRate, useDeleteTaxRate,
  getListTaxTypesQueryKey,
  useListPayrollTaxConfigs, useCreatePayrollTaxConfig, useUpdatePayrollTaxConfig, useDeletePayrollTaxConfig,
  getListPayrollTaxConfigsQueryKey,
  useListMembers, useListInvitations, useSendInvite,
  useLogout,
  type Customer, type MasterSC, type MasterSupplier, type MasterStaffMember, type ExpenseCategory,
  type Company, type MemberItem, type InvitationItem, type TaxTypeWithRates, type TaxRate,
  type PayrollTaxConfig,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Building2, Plus, LayoutDashboard, FolderKanban, Users, Settings as SettingsIcon,
  LogOut, Briefcase, Wallet, Mail, CheckCircle2, Clock, Loader2, UserPlus, Shield,
  Trash2, Bell, TrendingUp, AlertTriangle, X, Lock, Menu, ChevronDown, ChevronRight, Pencil, Star, Info,
  BarChart2, Target, Activity, TrendingDown, CheckCircle, HardHat, PiggyBank, Plug, BookOpen
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { KpiCard } from "@/components/ui/kpi-card";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { apiGet, apiPut } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, ChevronsUpDown } from "lucide-react";
import { fc, N, fp, uid, separateAdvances, calcClientAdvanceRecovery } from "@/lib/finance";
import { PLANS as PLAN_DEFS, getPlan, hasFeature, isWithinLimit, type PlanDef } from "@/lib/plans";
import { COUNTRIES, CURRENCIES, getCurrencySymbol } from "@/lib/iso";
import { DemoBanner } from "@/components/DemoBanner";
import { useDemoProfile } from "@/context/DemoProfileContext";

import type { Project } from "@workspace/api-client-react";
import CFODashboardTab from "./CFODashboardTab";
import IntegrationsTab from "./IntegrationsTab";
import AccountingModule from "./AccountingModule";
import { UpgradeTeaser } from "@/components/UpgradeTeaser";


const INDUSTRIES = [
  "Construction & Civil Engineering","Infrastructure","Real Estate Development","Oil & Gas Construction",
  "Industrial & Manufacturing","MEP & Fit-Out","Roads & Highways","Marine & Offshore","Power & Energy",
  "Water & Wastewater","Telecommunications Infrastructure","Property Development","Architecture & Design",
  "Engineering Consultancy","Project Management","Facility Management","Other",
];

const TAX_LABELS = ["VAT","GST","Sales Tax","HST","PST","SST","TPS/TVQ","No Tax"];

interface Alert { id: string; type: "warning" | "danger"; title: string; desc: string; }

function AlertsBell({ companyId }: { companyId: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const today = new Date();
  const in30 = new Date(today); in30.setDate(today.getDate() + 30);

  const { data: invoices = [] } = useListClientInvoices({ companyId });
  const { data: receipts = [] } = useListClientReceipts({ companyId });
  const { data: notes = [] } = useListPromissoryNotes({ companyId });
  const { data: scInvoices = [] } = useListScInvoices({ companyId });
  const { data: scPayments = [] } = useListScPayments({ companyId });
  const { data: supplierInvoices = [] } = useListSupplierInvoices({ companyId });
  const { data: supplierPayments = [] } = useListSupplierPayments({ companyId });
  const { data: budgetLines = [] } = useListBudget({ companyId });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const receivedByInv: Record<string, number> = {};
  for (const r of receipts) {
    if (r.tp === 'Advance mobilisation' || r.tp === 'Advance') continue;
    receivedByInv[r.invRef] = (receivedByInv[r.invRef] ?? 0) + N(r.amount);
  }
  const scPaidByInv: Record<string, number> = {};
  for (const p of scPayments) {
    if (p.tp === 'Advance') continue;
    scPaidByInv[p.invRef] = (scPaidByInv[p.invRef] ?? 0) + N(p.amount);
  }
  const suppPaidByInv: Record<string, number> = {};
  for (const p of supplierPayments) {
    suppPaidByInv[p.invRef] = (suppPaidByInv[p.invRef] ?? 0) + N(p.amount);
  }

  const alerts: Alert[] = [];

  for (const inv of invoices) {
    const gross = N(inv.gross);
    const received = receivedByInv[inv.id] ?? 0;
    const outstanding = gross - received;
    if (outstanding > 0.01) {
      const invDate = inv.date ? new Date(inv.date) : null;
      const daysSince = invDate ? Math.floor((today.getTime() - invDate.getTime()) / 86400000) : 0;
      if (daysSince >= 60) {
        alerts.push({ id: `inv-${inv.id}`, type: "danger", title: "Overdue Client Invoice", desc: `${inv.desc || inv.id} — ${fc(outstanding)} outstanding (${daysSince}d old)` });
      } else if (daysSince >= 30) {
        alerts.push({ id: `inv-${inv.id}`, type: "warning", title: "Outstanding Client Invoice", desc: `${inv.desc || inv.id} — ${fc(outstanding)} unpaid (${daysSince}d)` });
      }
    }
  }

  for (const inv of scInvoices) {
    const gross = N(inv.gross);
    const ret = gross * N(inv.retPct);
    const net = gross - ret;
    const paid = scPaidByInv[inv.id] ?? 0;
    const bal = net - paid;
    if (bal > 0.01) {
      const invDate = inv.date ? new Date(inv.date) : null;
      const daysSince = invDate ? Math.floor((today.getTime() - invDate.getTime()) / 86400000) : 0;
      if (daysSince >= 45) {
        alerts.push({ id: `sci-${inv.id}`, type: "warning", title: "SC Payment Overdue", desc: `${inv.scName || inv.id} — ${fc(bal)} outstanding (${daysSince}d)` });
      }
    }
  }

  for (const inv of supplierInvoices) {
    const gross = N(inv.gross);
    const paid = suppPaidByInv[inv.id] ?? 0;
    const bal = gross - paid;
    if (bal > 0.01) {
      const invDate = inv.date ? new Date(inv.date) : null;
      const daysSince = invDate ? Math.floor((today.getTime() - invDate.getTime()) / 86400000) : 0;
      if (daysSince >= 45) {
        alerts.push({ id: `si-${inv.id}`, type: "warning", title: "Supplier Payment Overdue", desc: `${inv.suppName || inv.id} — ${fc(bal)} unpaid (${daysSince}d)` });
      }
    }
  }

  const budgetByCategory: Record<string, { budget: number; actual: number }> = {};
  for (const b of budgetLines) {
    const cat = b.cat || "Uncategorized";
    if (!budgetByCategory[cat]) budgetByCategory[cat] = { budget: 0, actual: 0 };
    budgetByCategory[cat].budget += N(b.appBgt);
    budgetByCategory[cat].actual += N(b.actl);
  }
  for (const [cat, { budget, actual }] of Object.entries(budgetByCategory)) {
    if (budget > 0 && actual > budget) {
      const overrun = actual - budget;
      const pct = ((overrun / budget) * 100).toFixed(0);
      alerts.push({ id: `bgt-${cat}`, type: "danger", title: "Budget Overrun", desc: `${cat} — ${fc(overrun)} over budget (${pct}% variance)` });
    } else if (budget > 0 && actual / budget >= 0.8) {
      alerts.push({ id: `bgt-warn-${cat}`, type: "warning", title: "Budget Nearing Limit", desc: `${cat} — ${((actual / budget) * 100).toFixed(0)}% utilized` });
    }
  }

  for (const note of notes) {
    if (note.dueDate && note.sts !== "Cleared" && note.sts !== "Bounced") {
      const due = new Date(note.dueDate);
      if (due <= in30) {
        const daysLeft = Math.floor((due.getTime() - today.getTime()) / 86400000);
        const overdue = daysLeft < 0;
        alerts.push({ id: `note-${note.id}`, type: overdue ? "danger" : "warning", title: overdue ? "Overdue Cheque/Note" : "Maturing Cheque/Note", desc: `${note.party ?? note.noteNo} — ${fc(N(note.amount))} ${overdue ? `(${-daysLeft}d overdue)` : `(due in ${daysLeft}d)`}` });
      }
    }
  }

  const count = alerts.length;
  const hasDanger = alerts.some(a => a.type === "danger");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 rounded-lg border border-white/25 flex items-center justify-center hover:bg-white/10 transition-colors"
        title="Alerts & Notifications"
      >
        <Bell
          className="w-4 h-4 text-white/80"
          style={count > 0 ? { animation: "bell-ring 0.8s ease-in-out" } : undefined}
        />
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            style={{ backgroundColor: hasDanger ? "#ef4444" : "#f59e0b" }}>
            <span className="absolute inset-0 rounded-full animate-ping opacity-60"
              style={{ backgroundColor: hasDanger ? "#ef4444" : "#f59e0b" }} />
            <span className="relative">{count > 9 ? "9+" : count}</span>
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-[340px] bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden"
          style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold text-sm">
                {count > 0 ? (
                  <>{count} Alert{count !== 1 ? "s" : ""} <span className={hasDanger ? "text-red-500" : "text-amber-500"}>({hasDanger ? "action required" : "review needed"})</span></>
                ) : "Alerts & Notifications"}
              </span>
            </div>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border/50">
            {alerts.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div className="font-medium text-sm text-foreground mb-1">All clear</div>
                <div className="text-xs text-muted-foreground">No outstanding alerts at this time</div>
              </div>
            ) : (
              <>
                {alerts.filter(a => a.type === "danger").map(a => (
                  <div key={a.id} className="flex gap-3 px-4 py-3 bg-red-500/5 hover:bg-red-500/10 transition-colors">
                    <div className="w-7 h-7 rounded-full bg-red-500/15 flex items-center justify-center shrink-0 mt-0.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-red-600 dark:text-red-400">{a.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{a.desc}</div>
                    </div>
                  </div>
                ))}
                {alerts.filter(a => a.type === "warning").map(a => (
                  <div key={a.id} className="flex gap-3 px-4 py-3 bg-amber-500/5 hover:bg-amber-500/10 transition-colors">
                    <div className="w-7 h-7 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-amber-600 dark:text-amber-400">{a.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{a.desc}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          {count > 0 && (
            <div className="px-4 py-2.5 border-t border-border bg-muted/20 text-xs text-muted-foreground text-center">
              {alerts.filter(a => a.type === "danger").length} critical · {alerts.filter(a => a.type === "warning").length} warnings
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CompanyPortal() {
  const { data: user } = useGetMe();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { profile: demoProfile, getEffectiveFeatures } = useDemoProfile();
  const logoutMutation = useLogout();

  const initialTab = (() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("integration_connected") || params.get("integration_error")) return "settings";
    }
    return "dashboard";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  const { data: companies = [], isLoading: companiesLoading, isError: companiesError } = useListCompanies();

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    queryClient.clear();
    setLocation("/");
  };

  const companyId = user?.companyId || "";
  const company = companies.find(c => c.id === companyId);

  const meCompany = user?.company;
  const isDemo = meCompany?.isDemo ?? false;

  const memberRole = (company as unknown as { memberRole?: string })?.memberRole;
  const isAdmin = user?.role === "Admin" || user?.role === "Super Admin"
    || memberRole === "Admin" || memberRole === "Owner";

  const ALL_TABS = [
    { id: "dashboard", label: "Financial Dashboard", icon: LayoutDashboard },
    { id: "cfo",       label: "CFO View",             icon: TrendingUp },
    { id: "projects",  label: "Projects",            icon: FolderKanban },
    { id: "accounting", label: "Accounting",          icon: BookOpen },
    { id: "masters",   label: "Masters",              icon: Users },
    { id: "team",      label: "Team",                 icon: UserPlus },
    { id: "settings",  label: "Company Settings",     icon: SettingsIcon },
  ];
  const TABS = ALL_TABS;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <DemoBanner
        isDemo={isDemo}
        companyId={companyId}
        companyName={company?.name}
        companyPlan={company?.plan}
        userEmail={user?.email}
      />
      <header className="h-16 border-b border-white/10 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-10 shadow-sm" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
        <div className="flex items-center gap-3">
          <img src="/images/finproject-logo.png" alt="FinProject" className="h-9 w-auto rounded-lg" style={{ border: "2px solid #ffffff", padding: "2px", background: "#ffffff" }} />
          <span className="font-bold text-lg text-white hidden sm:inline">{company?.name ?? "FinProject"}</span>
          <span className="font-bold text-base text-white sm:hidden">{(company?.name ?? "FinProject").length > 14 ? (company?.name ?? "FinProject").slice(0, 14) + "…" : (company?.name ?? "FinProject")}</span>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === t.id
                  ? "text-slate-900"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
              style={activeTab === t.id ? { background: "#C9A840" } : {}}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:flex flex-col items-end">
            <span className="text-sm font-medium text-white">{user?.name}</span>
            <span className="text-xs text-white/50">{user?.role}</span>
          </div>
          <AlertsBell companyId={companyId} />
          <Button variant="outline" size="sm" className="hidden sm:flex rounded-lg border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-1" /> Sign Out
          </Button>
          {/* Mobile hamburger */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden w-9 h-9 rounded-lg border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
                <Menu className="w-4 h-4 text-white/70" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
              <SheetHeader className="p-5 border-b border-white/10">
                <SheetTitle className="text-left text-base text-white">{company?.name ?? "FinProject"}</SheetTitle>
                <div className="text-xs text-white/50 text-left">{user?.name} · {user?.role}</div>
              </SheetHeader>
              <nav className="p-4 space-y-1">
                {TABS.map(t => (
                  <button
                    key={t.id}
                    onClick={() => { setActiveTab(t.id); setMobileNavOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                      activeTab === t.id
                        ? "text-slate-900"
                        : "text-white/70 hover:text-white hover:bg-white/10"
                    }`}
                    style={activeTab === t.id ? { background: "#C9A840" } : {}}
                  >
                    <t.icon className="w-4 h-4 shrink-0" />
                    {t.label}
                  </button>
                ))}
              </nav>
              <div className="px-4 pb-4 border-t border-white/10 pt-4 mt-2">
                <Button variant="outline" size="sm" className="w-full rounded-lg border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => { setMobileNavOpen(false); handleLogout(); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1 p-4 sm:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {!company && companiesLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading workspace…</span>
              </div>
            </div>
          )}
          {!company && !companiesLoading && companiesError && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-muted-foreground">
                <p className="font-medium text-destructive">Failed to load workspace data.</p>
                <p className="text-sm mt-1">Please refresh the page. If the problem persists, try signing out and back in.</p>
              </div>
            </div>
          )}
          {!company && !companiesLoading && !companiesError && companyId && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-muted-foreground">
                <p className="font-medium">Workspace not accessible.</p>
                <p className="text-sm mt-1">Your session may have expired. Please sign out and sign back in.</p>
              </div>
            </div>
          )}
          {company && activeTab === "dashboard" && <DashboardTab companyId={companyId} company={company} />}
          {company && activeTab === "cfo" && (() => {
            const cfoIsDemo = !!(user?.company?.isDemo);
            const cfoEffPlan = (cfoIsDemo && demoProfile?.planId) ? demoProfile.planId : (company?.plan ?? "pro");
            const cfoFeat = getEffectiveFeatures(cfoEffPlan);
            return cfoFeat.cfo
              ? <CFODashboardTab companyId={companyId} company={company} effectiveFeatures={cfoFeat} effectivePlan={cfoEffPlan} />
              : <UpgradeTeaser feature="CFO View" requiredPlan="Lite Pro or higher" />;
          })()}
          {company && activeTab === "projects"  && <ProjectsTab  companyId={companyId} company={company} />}
          {company && activeTab === "accounting" && (() => {
            const accIsDemo = !!(user?.company?.isDemo);
            const accEffPlan = (accIsDemo && demoProfile?.planId) ? demoProfile.planId : (company?.plan ?? "pro");
            const accFeat = getEffectiveFeatures(accEffPlan);
            return accFeat.accounting
              ? <AccountingModule companyId={companyId} companyName={company?.name} plan={accEffPlan} />
              : <UpgradeTeaser feature="Double-Entry Accounting Module" requiredPlan="Pro Plus ($100/mo)" />;
          })()}
          {company && activeTab === "masters"   && <MastersTab   companyId={companyId} company={company} />}
          {activeTab === "team"      && <TeamTab      companyId={companyId} />}
          {company && activeTab === "settings"  && <SettingsTab  companyId={companyId} company={company} />}
        </div>
      </main>
    </div>
  );
}

/* ─── Upgrade teaser for plan-locked features — imported from @/components/UpgradeTeaser ───*/

/* ═══════════════════════════════════════════════════════ DASHBOARD */
function DashboardTab({ companyId, company }: { companyId: string; company: Company }) {
  const cur = company?.currency;
  const { data: dashUser } = useGetMe();
  const { profile: dashDemoProfile, getEffectiveFeatures: dashGetEff } = useDemoProfile();
  const dashIsDemo = !!(dashUser?.company?.isDemo);
  const effectivePlan = (dashIsDemo && dashDemoProfile?.planId) ? dashDemoProfile.planId : (company?.plan ?? "pro");
  const feat = dashGetEff(effectivePlan);

  const { data: projects = [] } = useListProjects({ companyId });
  const { data: invoices = [] } = useListClientInvoices({ companyId });
  const { data: receipts = [] } = useListClientReceipts({ companyId });
  const { data: scInvoices = [] } = useListScInvoices({ companyId });
  const { data: scPayments = [] } = useListScPayments({ companyId });
  const { data: supplierInvoices = [] } = useListSupplierInvoices({ companyId });
  const { data: supplierPayments = [] } = useListSupplierPayments({ companyId });
  const { data: notes = [] } = useListPromissoryNotes({ companyId });
  const { data: budgetLines = [] } = useListBudget({ companyId });
  const { data: clientDocs = [] } = useListClientDocuments({ companyId });
  const { data: vendorOrders = [] } = useListVendorOrders({ companyId });

  const totalContract  = projects.reduce((s, p) => s + N(p.ctrVal), 0);
  const totalInvoiced  = invoices.reduce((s, i) => s + N(i.gross), 0);
  const { progressTotal: totalProgressCollected, advanceTotal: totalAdvanceReceived, retentionReleaseTotal: totalRetentionReleased } = separateAdvances(receipts);
  const totalCollected = totalProgressCollected + totalRetentionReleased;
  const totalRetentionHeld = invoices.reduce((s, i) => s + N(i.gross) * N(i.retPct), 0);
  const totalRetentionReceivable = totalRetentionHeld - totalRetentionReleased;
  const arBalance      = totalInvoiced - (totalProgressCollected + totalRetentionReleased);
  const totalAdvanceRecovered = projects.reduce((s, p) => s + calcClientAdvanceRecovery(
    receipts.filter(r => (r.tp === 'Advance mobilisation' || r.tp === 'Advance') && r.projectId === p.id).reduce((a, r) => a + N(r.amount), 0),
    p.ctrVal,
    invoices.filter(i => i.projectId === p.id).reduce((a, i) => a + N(i.gross), 0),
  ), 0);
  const totalAdvanceOutstanding = Math.max(0, totalAdvanceReceived - totalAdvanceRecovered);
  const activeCount    = projects.filter(p => p.status === "Active").length;

  // AR Aging buckets
  const now = Date.now();
  const arAging = { current: 0, d30: 0, d60: 0, d90plus: 0 };
  const receivedByInv: Record<string, number> = {};
  for (const r of receipts) { if (r.tp === 'Advance mobilisation' || r.tp === 'Advance') continue; receivedByInv[r.invRef] = (receivedByInv[r.invRef] ?? 0) + N(r.amount); }
  const overdueInvoices: { desc: string; bal: number; days: number }[] = [];
  for (const inv of invoices) {
    const bal = N(inv.gross) - (receivedByInv[inv.id] ?? 0);
    if (bal > 0.01) {
      const agingDate = inv.dueDate || inv.date;
      const days = agingDate ? Math.floor((now - new Date(agingDate).getTime()) / 86400000) : 0;
      if (days < 30) arAging.current += bal;
      else if (days < 60) arAging.d30 += bal;
      else if (days < 90) arAging.d60 += bal;
      else { arAging.d90plus += bal; overdueInvoices.push({ desc: inv.desc || inv.id, bal, days }); }
    }
  }

  // AP Aging
  const scPaidByInv: Record<string, number> = {};
  for (const p of scPayments) { if (p.tp === 'Advance') continue; scPaidByInv[p.invRef] = (scPaidByInv[p.invRef] ?? 0) + N(p.amount); }
  const suppPaidByInv: Record<string, number> = {};
  for (const p of supplierPayments) suppPaidByInv[p.invRef] = (suppPaidByInv[p.invRef] ?? 0) + N(p.amount);
  const totalScOutstanding = scInvoices.reduce((s, inv) => {
    const net = N(inv.gross) - N(inv.gross) * N(inv.retPct);
    return s + Math.max(0, net - (scPaidByInv[inv.id] ?? 0));
  }, 0);
  const totalSuppOutstanding = supplierInvoices.reduce((s, inv) => {
    return s + Math.max(0, N(inv.gross) - (suppPaidByInv[inv.id] ?? 0));
  }, 0);
  const totalAPBalance = totalScOutstanding + totalSuppOutstanding;

  // Retention summary
  const retHeld = invoices.reduce((s, i) => s + N(i.gross) * N(i.retPct), 0);
  const retReleased = receipts.filter(r => r.tp === 'Retention Release').reduce((s, r) => s + N(r.amount), 0);
  const retNetHeld = retHeld - retReleased;
  const scRetHeld = scInvoices.reduce((s, i) => s + N(i.gross) * N(i.retPct), 0);
  const scRetReleased = scPayments.filter(p => p.tp === 'Retention Release').reduce((s, p) => s + N(p.amount), 0);

  // Budget overview
  const totalBudget = budgetLines.reduce((s, b) => s + N(b.appBgt), 0);
  const totalActual = budgetLines.reduce((s, b) => s + N(b.actl), 0);
  const budgetVariance = totalBudget - totalActual;

  // Construction performance KPIs
  const billingRate = totalContract > 0 ? (totalInvoiced / totalContract) * 100 : 0;
  const collectionRate = totalInvoiced > 0 ? (totalCollected / totalInvoiced) * 100 : 0;
  const budgetUtilPct = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;
  const wip = Math.max(0, totalContract - totalInvoiced);
  const totalCostsPaid = [...scPayments, ...supplierPayments].reduce((s, p) => s + N(p.amount), 0);
  const grossProfit = totalCollected - totalCostsPaid;
  const grossMarginPct = totalCollected > 0 ? (grossProfit / totalCollected) * 100 : 0;
  const completedCount = projects.filter(p => p.status === "Completed").length;

  // Promissory notes summary
  const activeNotes = notes.filter(n => n.sts !== 'Cleared' && n.sts !== 'Bounced');
  const notesValue = activeNotes.reduce((s, n) => s + N(n.amount), 0);
  const overdueNotes = activeNotes.filter(n => n.dueDate && new Date(n.dueDate) < new Date());

  // Quotations summary
  const quotations = clientDocs.filter(d => d.docType === 'quotation');
  const quoTotal = quotations.reduce((s, d) => s + N(d.grossAmount), 0);
  const quoByStatus: Record<string, { count: number; value: number }> = {};
  for (const d of quotations) {
    const st = d.status || 'Draft';
    if (!quoByStatus[st]) quoByStatus[st] = { count: 0, value: 0 };
    quoByStatus[st].count++;
    quoByStatus[st].value += N(d.grossAmount);
  }
  const QUO_STATUSES = ['Draft', 'Sent', 'Accepted', 'Rejected'];
  const quoStatusColor: Record<string, string> = {
    Draft: 'text-muted-foreground', Sent: 'text-blue-600', Accepted: 'text-emerald-600', Rejected: 'text-rose-600',
  };

  // Purchase Orders summary
  const purchaseOrders = vendorOrders.filter(o => o.docType === 'po');
  const poTotal = purchaseOrders.reduce((s, o) => s + N(o.contractedValue), 0);
  const PO_STATUS_MAP: Record<string, string> = { PartialDelivery: 'Approved', Fulfilled: 'Completed' };
  const poByStatus: Record<string, { count: number; value: number }> = {};
  for (const o of purchaseOrders) {
    const rawSt = o.status || 'Draft';
    const st = PO_STATUS_MAP[rawSt] ?? rawSt;
    if (!poByStatus[st]) poByStatus[st] = { count: 0, value: 0 };
    poByStatus[st].count++;
    poByStatus[st].value += N(o.contractedValue);
  }
  const PO_STATUSES = ['Draft', 'Issued', 'Approved', 'Completed', 'Cancelled'];
  const poStatusColor: Record<string, string> = {
    Draft: 'text-muted-foreground', Issued: 'text-blue-600', Approved: 'text-emerald-600', Completed: 'text-teal-600', Cancelled: 'text-rose-600',
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Financial Dashboard</h2>
          <p className="text-muted-foreground text-sm mt-0.5">{activeCount} active · {projects.length} total projects</p>
        </div>
      </div>

      {/* Top KPIs — adaptive per plan */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {feat.clientRevenue ? (
          <KpiCard title="Total Contract Value" value={fc(totalContract, cur)} icon={Briefcase} />
        ) : (
          <KpiCard title="Active Projects" value={`${activeCount} / ${projects.length}`} icon={FolderKanban} />
        )}
        {feat.budget && <KpiCard title="Approved Budget" value={fc(totalBudget, cur)} icon={Wallet} />}
        {feat.budget && <KpiCard title="Actual Spent"    value={fc(totalActual, cur)}  icon={TrendingUp} trend={totalActual > totalBudget ? "down" : "up"} />}
        {feat.clientRevenue ? (
          <KpiCard title="AR Balance"  value={fc(arBalance, cur)} icon={Wallet} trend={arBalance > 0 ? "down" : "up"} />
        ) : (
          <KpiCard title="Budget Variance" value={fc(budgetVariance, cur)} icon={TrendingUp} trend={budgetVariance >= 0 ? "up" : "down"} />
        )}
        {feat.subcontractors && <KpiCard title="AP Balance" value={fc(totalAPBalance, cur)} icon={Wallet} trend={totalAPBalance > 0 ? "down" : "up"} />}
        {feat.clientRevenue && feat.subcontractors && (
          <KpiCard title="Net Cash" value={fc(totalCollected - totalAPBalance, cur)} icon={TrendingUp} trend="up" />
        )}
      </div>

      {/* Construction Performance KPIs */}
      {feat.clientRevenue && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {/* Billing Rate */}
          <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all group">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center"><BarChart2 className="w-4 h-4 text-blue-600" /></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Billing Rate</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{billingRate.toFixed(1)}%</div>
            <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, billingRate)}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">
              {fc(totalInvoiced, cur)} billed of {fc(totalContract, cur)}
            </div>
          </div>
          {/* Collection Rate */}
          <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all group">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950 flex items-center justify-center"><Target className="w-4 h-4 text-emerald-600" /></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Collection Rate</span>
            </div>
            <div className={`text-2xl font-bold ${collectionRate >= 80 ? 'text-emerald-600' : collectionRate >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{collectionRate.toFixed(1)}%</div>
            <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full transition-all ${collectionRate >= 80 ? 'bg-emerald-500' : collectionRate >= 50 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, collectionRate)}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">
              {fc(totalCollected, cur)} collected of {fc(totalInvoiced, cur)}
            </div>
          </div>
          {/* Gross Margin */}
          <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all group">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-950 flex items-center justify-center"><PiggyBank className="w-4 h-4 text-violet-600" /></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Gross Margin</span>
            </div>
            <div className={`text-2xl font-bold ${grossMarginPct >= 20 ? 'text-emerald-600' : grossMarginPct >= 0 ? 'text-amber-600' : 'text-rose-600'}`}>{grossMarginPct.toFixed(1)}%</div>
            <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full transition-all ${grossMarginPct >= 20 ? 'bg-violet-500' : grossMarginPct >= 0 ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, Math.max(0, grossMarginPct))}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">
              {fc(grossProfit, cur)} profit on {fc(totalCollected, cur)} collected
            </div>
          </div>
          {/* Work in Progress */}
          <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm hover:shadow-lg transition-all group">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center"><HardHat className="w-4 h-4 text-orange-600" /></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Work in Progress</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{fc(wip, cur)}</div>
            <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-orange-400 rounded-full transition-all" style={{ width: `${Math.min(100, 100 - billingRate)}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">
              Remaining unbilled · {(100 - billingRate).toFixed(1)}% of contracts
            </div>
          </div>
        </div>
      )}

      {/* Project Health Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center"><Activity className="w-5 h-5 text-blue-600" /></div>
          <div>
            <div className="text-2xl font-bold">{activeCount}</div>
            <div className="text-xs text-muted-foreground">Active Projects</div>
          </div>
        </div>
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
          <div>
            <div className="text-2xl font-bold">{completedCount}</div>
            <div className="text-xs text-muted-foreground">Completed Projects</div>
          </div>
        </div>
        <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-950 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
          <div>
            <div className="text-2xl font-bold text-rose-600">{fc(arAging.d90plus, cur)}</div>
            <div className="text-xs text-muted-foreground">Overdue AR (90+ days)</div>
          </div>
        </div>
        {feat.budget ? (
          <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-amber-600" /></div>
            <div>
              <div className={`text-2xl font-bold ${budgetUtilPct > 100 ? 'text-rose-600' : 'text-amber-600'}`}>{budgetUtilPct.toFixed(1)}%</div>
              <div className="text-xs text-muted-foreground">Budget Utilization</div>
            </div>
          </div>
        ) : (
          <div className="bg-card p-5 rounded-2xl border border-border/50 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-950 flex items-center justify-center"><Briefcase className="w-5 h-5 text-violet-600" /></div>
            <div>
              <div className="text-2xl font-bold">{projects.length}</div>
              <div className="text-xs text-muted-foreground">Total Projects</div>
            </div>
          </div>
        )}
      </div>

      {/* AR Aging + AP Overview — clientRevenue required */}
      {feat.clientRevenue && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl border-border/50">
          <h3 className="font-bold text-base mb-4">AR Aging Analysis</h3>
          <div className="space-y-3">
            {[
              { label: "Current (< 30 days)", val: arAging.current, color: "bg-emerald-500" },
              { label: "30–60 days",           val: arAging.d30,    color: "bg-amber-400" },
              { label: "60–90 days",           val: arAging.d60,    color: "bg-orange-500" },
              { label: "90+ days (Critical)",  val: arAging.d90plus,color: "bg-rose-600" },
            ].map(({ label, val, color }) => {
              const pct = arBalance > 0 ? Math.min(100, (val / arBalance) * 100) : 0;
              return (
                <div key={label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold">{fc(val, cur)}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-border/50 flex justify-between text-sm font-bold">
              <span>Total AR Outstanding</span>
              <span className="text-rose-600">{fc(arBalance, cur)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-border/50">
          <h3 className="font-bold text-base mb-4">AP & Payables Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground">SC Outstanding Balance</span>
              <span className="font-semibold text-rose-600">{fc(totalScOutstanding, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground">Supplier Outstanding Balance</span>
              <span className="font-semibold text-rose-600">{fc(totalSuppOutstanding, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30 font-bold">
              <span className="text-sm">Total AP Balance</span>
              <span className="text-rose-600">{fc(totalAPBalance, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground">Total Client Invoiced</span>
              <span className="font-semibold">{fc(totalInvoiced, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground">Progress Collected</span>
              <span className="font-semibold text-emerald-600">{fc(totalProgressCollected, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground">Retention Released</span>
              <span className="font-semibold text-emerald-600">{fc(totalRetentionReleased, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground">Advance Received</span>
              <span className="font-semibold text-orange-600">{fc(totalAdvanceReceived, cur)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-border/30">
              <span className="text-sm text-muted-foreground pl-4">Outstanding Advance (Deferred Liability)</span>
              <span className="font-semibold text-orange-700">{fc(totalAdvanceOutstanding, cur)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-muted-foreground">Retention Receivable</span>
              <span className="font-semibold text-amber-600">{fc(totalRetentionReceivable, cur)}</span>
            </div>
          </div>
        </Card>
      </div>}

      {/* Budget Overview — always visible */}
      {feat.budget && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Retention Summary — pro+ only */}
        {feat.retention ? (
          <Card className="p-5 rounded-2xl border-border/50">
            <h3 className="font-bold text-sm mb-3">Retention Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Retention Held (from clients)</span><span className="font-semibold">{fc(retHeld, cur)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Released to Clients</span><span className="text-emerald-600">{fc(retReleased, cur)}</span></div>
              <div className="flex justify-between border-t border-border/40 pt-2 font-bold"><span>Net Ret. Held</span><span className="text-amber-600">{fc(retNetHeld, cur)}</span></div>
              <div className="flex justify-between pt-1"><span className="text-muted-foreground">SC Retention Held</span><span className="font-semibold">{fc(scRetHeld, cur)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">SC Ret. Released</span><span className="text-emerald-600">{fc(scRetReleased, cur)}</span></div>
            </div>
          </Card>
        ) : (
          <UpgradeTeaser feature="Retention Tracking" requiredPlan="Lite Pro or higher" compact />
        )}
        <Card className="p-5 rounded-2xl border-border/50">
          <h3 className="font-bold text-sm mb-3">Budget Overview</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Approved Budget</span><span className="font-semibold">{fc(totalBudget, cur)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Actual Spent</span><span className="font-semibold">{fc(totalActual, cur)}</span></div>
            <div className={`flex justify-between border-t border-border/40 pt-2 font-bold`}>
              <span>Variance</span>
              <span className={budgetVariance >= 0 ? "text-emerald-600" : "text-rose-600"}>{budgetVariance >= 0 ? "+" : ""}{fc(budgetVariance, cur)}</span>
            </div>
            {totalBudget > 0 && (
              <div className="mt-2">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${totalActual > totalBudget ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, (totalActual / totalBudget) * 100)}%` }} />
                </div>
                <div className="text-xs text-muted-foreground mt-1">{((totalActual / totalBudget) * 100).toFixed(1)}% utilized</div>
              </div>
            )}
          </div>
        </Card>

        {feat.promissory ? (
          <Card className="p-5 rounded-2xl border-border/50">
            <h3 className="font-bold text-sm mb-3">Promissory Notes / PDCs</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Active Notes</span><span className="font-semibold">{activeNotes.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Value</span><span className="font-semibold">{fc(notesValue, cur)}</span></div>
              <div className="flex justify-between border-t border-border/40 pt-2">
                <span className="text-muted-foreground">Overdue</span>
                <span className={overdueNotes.length > 0 ? "font-bold text-rose-600" : "font-semibold text-emerald-600"}>{overdueNotes.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Maturing (30d)</span>
                <span className="font-semibold text-amber-600">{activeNotes.filter(n => {
                  if (!n.dueDate) return false;
                  const d = new Date(n.dueDate); const td = new Date();
                  return d > td && d <= new Date(td.setDate(td.getDate() + 30));
                }).length}</span>
              </div>
            </div>
          </Card>
        ) : (
          <UpgradeTeaser feature="Promissory Notes & PDC" requiredPlan="Lite Pro or higher" compact />
        )}
      </div>}

      {/* Overdue Receivables — clientRevenue only */}
      {feat.clientRevenue && overdueInvoices.length > 0 && (
        <Card className="rounded-2xl border-rose-200 bg-rose-50/30">
          <div className="px-6 py-4 border-b border-rose-100">
            <h3 className="font-bold text-base text-rose-700">Critical Overdue Receivables (90+ days)</h3>
          </div>
          <div className="divide-y divide-rose-100">
            {overdueInvoices.slice(0, 5).map((inv, i) => (
              <div key={i} className="flex justify-between items-center px-6 py-3">
                <div>
                  <div className="text-sm font-medium">{inv.desc}</div>
                  <div className="text-xs text-rose-500">{inv.days} days old</div>
                </div>
                <span className="font-bold text-rose-700 text-sm">{fc(inv.bal, cur)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quotations & POs — commercialDocs only */}
      {feat.commercialDocs && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 rounded-2xl border-border/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-base">Quotations</h3>
            <span className="text-xs text-muted-foreground">{quotations.length} total · {fc(quoTotal, cur)}</span>
          </div>
          <div className="space-y-2 text-sm">
            {QUO_STATUSES.map(st => {
              const entry = quoByStatus[st];
              if (!entry) return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className="text-muted-foreground">{st}</span>
                  <span className="text-muted-foreground">—</span>
                </div>
              );
              return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className={quoStatusColor[st] ?? 'text-muted-foreground'}>{st} <span className="text-muted-foreground">({entry.count})</span></span>
                  <span className={`font-semibold ${quoStatusColor[st] ?? ''}`}>{fc(entry.value, cur)}</span>
                </div>
              );
            })}
            <div className="flex justify-between py-1.5 font-bold">
              <span>Total Pipeline</span>
              <span>{fc(quoTotal, cur)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl border-border/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-base">Purchase Orders</h3>
            <span className="text-xs text-muted-foreground">{purchaseOrders.length} total · {fc(poTotal, cur)}</span>
          </div>
          <div className="space-y-2 text-sm">
            {PO_STATUSES.map(st => {
              const entry = poByStatus[st];
              if (!entry) return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className="text-muted-foreground">{st}</span>
                  <span className="text-muted-foreground">—</span>
                </div>
              );
              return (
                <div key={st} className="flex justify-between py-1.5 border-b border-border/30">
                  <span className={poStatusColor[st] ?? 'text-muted-foreground'}>{st} <span className="text-muted-foreground">({entry.count})</span></span>
                  <span className={`font-semibold ${poStatusColor[st] ?? ''}`}>{fc(entry.value, cur)}</span>
                </div>
              );
            })}
            <div className="flex justify-between py-1.5 font-bold">
              <span>Total Contracted</span>
              <span>{fc(poTotal, cur)}</span>
            </div>
          </div>
        </Card>
      </div>}

      {/* Active Projects + Recent Collections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl border-border/50">
          <h3 className="font-bold text-lg mb-4">Active Projects</h3>
          <div className="space-y-3">
            {projects.filter(p => p.status === "Active").slice(0, 6).map(p => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {(p.clientName || p.client) ? `Client: ${p.clientName || p.client}` : "—"}
                    {p.owner ? ` · Owner: ${p.owner}` : ""}
                  </div>
                </div>
                <div className="text-right ml-4 shrink-0">
                  <div className="font-medium">{fc(p.ctrVal, cur)}</div>
                  <div className="text-xs text-muted-foreground">{fp(p.comp)} complete</div>
                </div>
              </div>
            ))}
            {projects.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No projects yet.</p>}
          </div>
        </Card>

        {feat.clientRevenue ? (
          <Card className="p-6 rounded-2xl border-border/50">
            <h3 className="font-bold text-lg mb-4">Recent Collections</h3>
            <div className="space-y-3">
              {receipts.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "")).slice(0, 6).map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl border-l-4 border-emerald-400">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{r.desc || r.invRef}</div>
                    <div className="text-xs text-muted-foreground">{r.date} · {r.invRef}</div>
                  </div>
                  <div className="font-bold text-emerald-600 ml-4 shrink-0">+{fc(r.amount, cur)}</div>
                </div>
              ))}
              {receipts.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No collections yet.</p>}
            </div>
          </Card>
        ) : (
          <UpgradeTeaser feature="Client Invoicing & Collections" requiredPlan="Lite or higher" />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ PROJECTS */
function ProjectsTab({ companyId, company }: { companyId: string; company: Company }) {
  const cur = company?.currency;
  const confirm = useConfirm();
  const { data: projects = [] } = useListProjects({ companyId });
  const { data: invoices = [] } = useListClientInvoices({ companyId });
  const { data: allStaff = [] } = useListMasterStaff({ companyId });
  const { data: customers = [] } = useListCustomers({ companyId });
  const activeStaff = allStaff.filter(s => s.status === "Active");
  const activeCustomers = customers.filter(c => c.status === "Active");
  const createProject = useCreateProject();
  const deleteProject = useDeleteProject();
  const queryClient = useQueryClient();
  const { data: tabUser } = useGetMe();
  const { profile: tabDemoProfile, getEffectiveFeatures: tabGetEff } = useDemoProfile();
  const tabIsDemo = !!(tabUser?.company?.isDemo);

  const [isOpen, setIsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [clientComboOpen, setClientComboOpen] = useState(false);

  const totalContract = projects.reduce((s, p) => s + N(p.ctrVal), 0);
  const totalInvoiced = invoices.reduce((s, i) => s + N(i.gross), 0);
  const activeCount   = projects.filter(p => p.status === "Active").length;

  const effectivePlan = (tabIsDemo && tabDemoProfile?.planId) ? tabDemoProfile.planId : (company?.plan ?? "pro");
  const planDef = { ...getPlan(effectivePlan), features: tabGetEff(effectivePlan) };
  const atProjectLimit = !isWithinLimit(effectivePlan, "maxProjects", projects.length);

  const defaultTaxRate = N(company?.taxRate) * 100 || 0;

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const vatPct = N(String(fd.get("vatRate") ?? 0)) / 100;
    const cltRetPct = N(String(fd.get("cltRet") ?? 0)) / 100;
    const scRetPct  = N(String(fd.get("scRet")  ?? 0)) / 100;
    const advRecPct = N(String(fd.get("advRec") ?? 0)) / 100;
    const clientIdVal = selectedClientId || null;
    const selectedCustomer = activeCustomers.find(c => c.id === clientIdVal);
    await createProject.mutateAsync({
      data: {
        id: `P-${uid()}`,
        companyId,
        name:        fd.get("name")    as string,
        code:        (fd.get("code")   as string) || null,
        client:      selectedCustomer?.name ?? "",
        clientId:    clientIdVal,
        owner:       fd.get("owner")   as string,
        manager:     fd.get("manager") as string,
        ctrVal:      fd.get("ctrVal")  as string,
        budget:      fd.get("budget")  as string,
        vatRate:     String(vatPct),
        cltRet:      String(cltRetPct),
        scRet:       String(scRetPct),
        advRec:      String(advRecPct),
        stDt:           fd.get("stDt")    as string,
        endDt:          fd.get("endDt")   as string,
        status:         fd.get("status")  as string || "Active",
        advanceMode:    (fd.get("advanceMode") as string) || "invoice",
        division:       (fd.get("division") as string) || null,
        locationCountry:(fd.get("locationCountry") as string) || null,
        locationCity:   (fd.get("locationCity") as string) || null,
      },
    });
    queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
    setIsOpen(false);
    setSelectedClientId("");
  };

  const handleDelete = async (id: string) => {
    if (!await confirm({ title: 'Delete Project', message: 'Delete this project? All project data will be permanently removed.', confirmLabel: 'Delete', destructive: true })) return;
    setDeletingId(id);
    try {
      await deleteProject.mutateAsync({ id });
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Projects</h2>
          <p className="text-muted-foreground text-sm mt-0.5">
            {activeCount} active · {projects.length} total
            {planDef.maxProjects !== -1 && <span className="ml-2 text-xs">({projects.length}/{planDef.maxProjects} on {planDef.label} plan)</span>}
          </p>
        </div>
        <Dialog open={isOpen && !atProjectLimit} onOpenChange={v => !atProjectLimit && setIsOpen(v)}>
          <DialogTrigger asChild>
            {atProjectLimit ? (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-4 py-2 text-sm cursor-not-allowed">
                <Lock className="w-4 h-4" />
                <span>Limit reached — upgrade plan</span>
              </div>
            ) : (
              <Button className="rounded-xl">
                <Plus className="w-4 h-4 mr-2" /> + New Project
              </Button>
            )}
          </DialogTrigger>
          <DialogContent className="max-w-full sm:max-w-[600px] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Project Name *</Label>
                  <Input name="name" required placeholder="e.g. North Tower Development Phase 1" />
                </div>
                <div className="space-y-1.5">
                  <Label>Project Code</Label>
                  <Input name="code" placeholder="e.g. NTD-001" />
                </div>
                <div className="space-y-1.5">
                  <Label>Client</Label>
                  <Popover open={clientComboOpen} onOpenChange={setClientComboOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent hover:text-accent-foreground"
                      >
                        <span className={selectedClientId ? "text-foreground" : "text-muted-foreground"}>
                          {selectedClientId
                            ? (activeCustomers.find(c => c.id === selectedClientId)?.name ?? "Select client")
                            : "Select a client"}
                        </span>
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search clients..." />
                        <CommandList>
                          <CommandEmpty>No clients found.</CommandEmpty>
                          <CommandGroup>
                            {activeCustomers.map(c => (
                              <CommandItem
                                key={c.id}
                                value={c.name}
                                onSelect={() => {
                                  setSelectedClientId(prev => prev === c.id ? "" : c.id);
                                  setClientComboOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${selectedClientId === c.id ? "opacity-100" : "opacity-0"}`} />
                                {c.name}{c.type ? ` — ${c.type}` : ""}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label>Owner</Label>
                  <Input name="owner" placeholder="Internal project owner" />
                </div>
                <div className="space-y-1.5">
                  <Label>Project Manager</Label>
                  <Select name="manager">
                    <SelectTrigger>
                      <SelectValue placeholder={activeStaff.length === 0 ? "No active staff found" : "Select a staff member"} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeStaff.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-muted-foreground text-center">No active staff members found</div>
                      ) : (
                        activeStaff.map(s => (
                          <SelectItem key={s.id} value={s.name}>
                            {s.name}{s.jobTitle ? ` — ${s.jobTitle}` : ""}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Contract Value ({cur})</Label>
                  <Input name="ctrVal" type="number" min="0" step="0.01" placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label>Approved Budget ({cur})</Label>
                  <Input name="budget" type="number" min="0" step="0.01" placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label>{company?.taxLabel || "Tax"} Rate</Label>
                  <div className="flex items-center h-10 px-3 rounded-xl border border-input bg-muted/30 gap-2">
                    <span className="text-sm font-semibold">{defaultTaxRate > 0 ? `${defaultTaxRate.toFixed(1)}%` : "—"}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0">from company</span>
                  </div>
                  <input type="hidden" name="vatRate" value={String(defaultTaxRate)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select name="status" defaultValue="Active">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="On Hold">On Hold</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Client Retention (%)</Label>
                  <Input name="cltRet" type="number" min="0" max="100" step="0.01" placeholder="10" />
                </div>
                <div className="space-y-1.5">
                  <Label>SC Retention (%)</Label>
                  <Input name="scRet" type="number" min="0" max="100" step="0.01" placeholder="10" />
                </div>
                <div className="space-y-1.5">
                  <Label>Advance Recovery (%)</Label>
                  <Input name="advRec" type="number" min="0" max="100" step="0.01" placeholder="20" />
                </div>
                <div className="space-y-1.5">
                  <Label>Start Date</Label>
                  <Input name="stDt" type="date" />
                </div>
                <div className="space-y-1.5">
                  <Label>End Date (Planned)</Label>
                  <Input name="endDt" type="date" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Advance Handling</Label>
                  <Select name="advanceMode" defaultValue="invoice">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice (default)</SelectItem>
                      <SelectItem value="receipt_only">Receipt Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Receipt Only hides the Advance invoice type; advance is recorded as a direct receipt.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Division</Label>
                  <Input name="division" placeholder="e.g. Infrastructure, Building" />
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Location</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <LocationPicker />
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full h-11 rounded-xl mt-2" disabled={createProject.isPending}>
                {createProject.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Project
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Total Contract Value" value={fc(totalContract, cur)} icon={Briefcase} />
        <KpiCard title="Total Invoiced"        value={fc(totalInvoiced, cur)} icon={Wallet} />
        <KpiCard title="Total Collected"       value={fc(0, cur)}             icon={TrendingUp} />
        <KpiCard title="Active Projects"       value={String(activeCount)}    icon={FolderKanban} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {projects.map(p => {
          const projInvoiced = invoices.filter(i => i.projectId === p.id).reduce((s, i) => s + N(i.gross), 0);
          return (
            <div key={p.id} className="bg-card border border-border/50 rounded-2xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0 pr-3">
                  <Link href={`/app/${company?.slug || companyId}/project/${p.id}`}>
                    <h3 className="font-bold text-base hover:text-primary transition-colors cursor-pointer truncate">{p.name}</h3>
                  </Link>
                  {p.code && <p className="text-xs font-mono text-muted-foreground">{p.code}</p>}
                  <p className="text-sm text-muted-foreground">
                    {(p.clientName || p.client) ? `Client: ${p.clientName || p.client}` : "No client"}
                    {p.owner ? ` · Owner: ${p.owner}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-emerald-100 text-emerald-700 border-none text-xs">{p.status}</Badge>
                  <button
                    onClick={() => handleDelete(p.id)}
                    disabled={deletingId === p.id}
                    className="text-destructive hover:bg-destructive/10 rounded-lg p-1.5 transition-colors"
                  >
                    {deletingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Contract Value</div>
                  <div className="font-semibold">{fc(p.ctrVal, cur)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Invoiced</div>
                  <div className="font-semibold">{fc(projInvoiced, cur)}</div>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.min(100, N(p.comp) * 100)}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{p.manager} · {p.stDt}</span>
                <span>{fp(p.comp)}</span>
              </div>
            </div>
          );
        })}
        {projects.length === 0 && (
          <div className="col-span-2 text-center py-16 text-muted-foreground bg-muted/20 rounded-2xl border border-dashed border-border">
            <FolderKanban className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No projects yet</p>
            <p className="text-sm">Create your first project to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ MASTERS */
function MastersTab({ companyId, company }: { companyId: string; company: Company }) {
  const { data: mastersUser } = useGetMe();
  const { profile: mastersDemoProfile, getEffectiveFeatures: mastersGetEff } = useDemoProfile();
  const mastersIsDemo = !!(mastersUser?.company?.isDemo);
  const mastersEffectivePlan = (mastersIsDemo && mastersDemoProfile?.planId) ? mastersDemoProfile.planId : (company?.plan ?? "pro");
  const mastersFeat = mastersGetEff(mastersEffectivePlan);

  const [section, setSection] = useState<"customers" | "scs" | "suppliers" | "staff" | "expensecats">("customers");
  const { data: customers  = [] } = useListCustomers({ companyId });
  const { data: masterScs  = [] } = useListMasterSCs({ companyId });
  const { data: masterSuppliers = [] } = useListMasterSuppliers({ companyId });
  const { data: masterStaff = [] } = useListMasterStaff({ companyId });
  const { data: expenseCategories = [] } = useListExpenseCategories({ companyId });
  const { data: allProjects = [] } = useListProjects({ companyId });
  const { data: allSubcontractors = [] } = useListSubcontractors({ companyId });
  const { data: allSuppliers = [] } = useListSuppliers({ companyId });
  const { data: allPayroll = [] } = useListPayroll({ companyId });
  const { data: allBudget = [] } = useListBudget({ companyId });
  const { data: allClientInvoices = [] } = useListClientInvoices({ companyId });
  const cur = company?.currency;

  const customerBlockMessages = new Map<string, string>();
  for (const c of customers) {
    const projCount = allProjects.filter(p => p.clientId === c.id || (p.client ?? "") === c.name).length;
    const invCount  = allClientInvoices.filter(i => i.customerId === c.id).length;
    if (projCount > 0 || invCount > 0) {
      const parts: string[] = [];
      if (projCount > 0) parts.push(`${projCount} project${projCount !== 1 ? "s" : ""}`);
      if (invCount  > 0) parts.push(`${invCount} invoice${invCount !== 1 ? "s" : ""}`);
      customerBlockMessages.set(c.id, `Linked to ${parts.join(" and ")}`);
    }
  }

  const scBlockMessages = new Map<string, string>();
  for (const sc of masterScs) {
    const count = allSubcontractors.filter(s =>
      !s.id.startsWith("master:") &&
      (s.masterId === sc.id || s.name === sc.name)
    ).length;
    if (count > 0) scBlockMessages.set(sc.id, `In use on ${count} project${count !== 1 ? "s" : ""}`);
  }

  const supplierBlockMessages = new Map<string, string>();
  for (const sup of masterSuppliers) {
    const count = allSuppliers.filter(s =>
      !s.id.startsWith("master:") &&
      (s.masterId === sup.id || s.name === sup.name)
    ).length;
    if (count > 0) supplierBlockMessages.set(sup.id, `In use on ${count} project${count !== 1 ? "s" : ""}`);
  }

  const staffBlockMessages = new Map<string, string>();
  for (const s of masterStaff) {
    const count = allPayroll.filter(p => p.name === s.name).length;
    if (count > 0) staffBlockMessages.set(s.id, `Has ${count} payroll record${count !== 1 ? "s" : ""}`);
  }

  const catBlockMessages = new Map<string, string>();
  for (const c of expenseCategories) {
    const count = allBudget.filter(b => b.cat === c.name).length;
    if (count > 0) catBlockMessages.set(c.id, `Used in ${count} budget item${count !== 1 ? "s" : ""}`);
  }

  const TABS = [
    { id: "customers",   label: `Clients`,            count: customers.length },
    { id: "scs",         label: `Subcontractors`,     count: masterScs.length },
    { id: "suppliers",   label: `Suppliers`,          count: masterSuppliers.length },
    { id: "staff",       label: `Staff / Payroll`,    count: masterStaff.length },
    { id: "expensecats", label: `Expense Categories`, count: expenseCategories.length },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Company Masters</h2>
        <p className="text-muted-foreground text-sm mt-0.5">Shared registers for clients, subcontractors, suppliers, staff and expense categories — available across all projects</p>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSection(t.id as typeof section)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
              section === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label} <span className="ml-1.5 text-xs bg-muted px-1.5 py-0.5 rounded-full">{t.count}</span>
          </button>
        ))}
      </div>

      {section === "customers"   && <CustomersTable        companyId={companyId} customers={customers} blockMessages={customerBlockMessages} />}
      {section === "scs"         && <SCsTable companyId={companyId} masterScs={masterScs} blockMessages={scBlockMessages} />}
      {section === "suppliers"   && <SuppliersTable companyId={companyId} masterSuppliers={masterSuppliers} cur={cur} blockMessages={supplierBlockMessages} />}
      {section === "staff"       && (mastersFeat.payroll ? <StaffTable companyId={companyId} masterStaff={masterStaff} cur={cur} blockMessages={staffBlockMessages} /> : <UpgradeTeaser feature="Staff & Payroll" requiredPlan="Lite or higher" />)}
      {section === "expensecats" && <ExpenseCategoriesTable companyId={companyId} expenseCategories={expenseCategories} blockMessages={catBlockMessages} />}
    </div>
  );
}

/* — Clients — */
function CustomersTable({ companyId, customers, blockMessages }: { companyId: string; customers: Customer[]; blockMessages: Map<string, string> }) {
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [formCountry, setFormCountry] = useState("");

  useEffect(() => { if (isOpen) setFormCountry(editing?.country || ""); }, [isOpen, editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/customers"] });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      name:    fd.get("name")    as string,
      type:    fd.get("type")    as string,
      contact: fd.get("contact") as string,
      phone:   fd.get("phone")   as string,
      email:   fd.get("email")   as string,
      country: fd.get("country") as string,
      vatNo:   fd.get("vatNo")   as string,
      status:  fd.get("status")  as string || "Active",
    };
    if (editing) {
      await updateCustomer.mutateAsync({ id: editing.id, data });
    } else {
      await createCustomer.mutateAsync({ data: { id: `CUS-${uid()}`, companyId, ...data } });
    }
    invalidate();
    setIsOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    const blockMsg = blockMessages.get(id);
    if (blockMsg) {
      await confirm({ title: "Cannot Delete Client", message: `This client cannot be deleted — it is ${blockMsg}. Remove all linked records first.`, confirmLabel: "OK", hideCancelButton: true, destructive: false });
      return;
    }
    if (!await confirm({ title: 'Delete Client', message: 'Delete this client? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await deleteCustomer.mutateAsync({ id });
      invalidate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
      toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
    }
  };

  const openEdit = (c: Customer) => { setEditing(c); setIsOpen(true); };
  const openNew  = ()            => { setEditing(null); setIsOpen(true); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> + Add Client</Button>
      </div>
      <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-full sm:max-w-[550px] rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Client Name *</Label>
                <Input name="name" required defaultValue={editing?.name} placeholder="Company or individual name" />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select name="type" defaultValue={editing?.type || "Company"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Company","Government","Individual","Joint Venture","NGO"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Select name="country" value={formCountry} onValueChange={setFormCountry}>
                  <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Contact Person</Label>
                <Input name="contact" defaultValue={editing?.contact} placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <PhoneInput name="phone" defaultValue={editing?.phone} country={formCountry} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input name="email" type="email" defaultValue={editing?.email} placeholder="contact@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Tax / VAT Number</Label>
                <Input name="vatNo" defaultValue={editing?.vatNo} placeholder="Registration number" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select name="status" defaultValue={editing?.status || "Active"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl">
              {editing ? "Save Changes" : "Add Client"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="rounded-2xl border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-24">ID</TableHead>
              <TableHead>Client Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Contact Person</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>VAT Number</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No clients registered. Add your first client above.</TableCell></TableRow>
            )}
            {customers.map(c => (
              <TableRow key={c.id}>
                <TableCell className="text-xs text-muted-foreground font-mono">{c.id}</TableCell>
                <TableCell>
                  <div className="font-medium">{c.name}</div>
                  {c.country && <div className="text-xs text-muted-foreground">{c.country}</div>}
                </TableCell>
                <TableCell>
                  {c.type && <Badge variant="outline" className="text-xs">{c.type}</Badge>}
                </TableCell>
                <TableCell>{c.contact || "—"}</TableCell>
                <TableCell>{c.phone || "—"}</TableCell>
                <TableCell className="text-primary text-sm">{c.email || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{c.vatNo || "—"}</TableCell>
                <TableCell>
                  <Badge className={c.status === "Active" ? "bg-emerald-100 text-emerald-700 border-none" : "bg-red-100 text-red-700 border-none"}>
                    {c.status || "Active"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(c.id)}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* — Subcontractors — */
function SCsTable({ companyId, masterScs, blockMessages }: { companyId: string; masterScs: MasterSC[]; blockMessages: Map<string, string> }) {
  const createSC = useCreateMasterSC();
  const updateSC = useUpdateMasterSC();
  const deleteSC = useDeleteMasterSC();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<MasterSC | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/master-scs"] });
    queryClient.invalidateQueries({ queryKey: ["/api/subcontractors"] });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      name:    fd.get("name")    as string,
      trade:   fd.get("trade")   as string,
      contact: fd.get("contact") as string,
      phone:   fd.get("phone")   as string,
      email:   fd.get("email")   as string,
      vatNo:   fd.get("vatNo")   as string,
      cr:      fd.get("cr")      as string,
      status:  fd.get("status")  as string || "Active",
    };
    if (editing) {
      await updateSC.mutateAsync({ id: editing.id, data });
    } else {
      await createSC.mutateAsync({ data: { id: `MSC-${uid()}`, companyId, ...data } });
    }
    invalidate();
    setIsOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    const blockMsg = blockMessages.get(id);
    if (blockMsg) {
      await confirm({ title: "Cannot Delete Subcontractor", message: `This subcontractor cannot be deleted — it is ${blockMsg}. Remove all project links first.`, confirmLabel: "OK", hideCancelButton: true, destructive: false });
      return;
    }
    if (!await confirm({ title: 'Delete Subcontractor', message: 'Delete this subcontractor? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await deleteSC.mutateAsync({ id });
      invalidate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
      toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
    }
  };

  const openEdit = (sc: MasterSC) => { setEditing(sc); setIsOpen(true); };
  const openNew  = ()             => { setEditing(null); setIsOpen(true); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> + Add Subcontractor</Button>
      </div>
      <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-full sm:max-w-[550px] rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Subcontractor" : "Add Subcontractor"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Company Name *</Label>
                <Input name="name" required defaultValue={editing?.name} placeholder="Subcontractor company name" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Trade / Scope of Work</Label>
                <Input name="trade" defaultValue={editing?.trade} placeholder="e.g. Structural Civil, MEP, Finishing" />
              </div>
              <div className="space-y-1.5">
                <Label>Contact Person</Label>
                <Input name="contact" defaultValue={editing?.contact} placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <PhoneInput name="phone" defaultValue={editing?.phone} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input name="email" type="email" defaultValue={editing?.email} placeholder="contact@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Tax / VAT Number</Label>
                <Input name="vatNo" defaultValue={editing?.vatNo} placeholder="Registration number" />
              </div>
              <div className="space-y-1.5">
                <Label>CR / Company Reg. No.</Label>
                <Input name="cr" defaultValue={editing?.cr} placeholder="Company registration number" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select name="status" defaultValue={editing?.status || "Active"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="Blacklisted">Blacklisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl">
              {editing ? "Save Changes" : "Add Subcontractor"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="rounded-2xl border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-24">ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Trade / Scope</TableHead>
              <TableHead>Contact Person</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>VAT No.</TableHead>
              <TableHead>CR No.</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {masterScs.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No subcontractors registered.</TableCell></TableRow>
            )}
            {masterScs.map(sc => (
              <TableRow key={sc.id}>
                <TableCell className="text-xs text-muted-foreground font-mono">{sc.id}</TableCell>
                <TableCell className="font-medium">{sc.name}</TableCell>
                <TableCell>
                  {sc.trade && <Badge variant="outline" className="text-xs whitespace-nowrap">{sc.trade}</Badge>}
                </TableCell>
                <TableCell>{sc.contact || "—"}</TableCell>
                <TableCell>{sc.phone || "—"}</TableCell>
                <TableCell className="text-primary text-sm">{sc.email || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{sc.vatNo || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{sc.cr || "—"}</TableCell>
                <TableCell>
                  <Badge className={sc.status === "Active" ? "bg-emerald-100 text-emerald-700 border-none" : sc.status === "Blacklisted" ? "bg-red-100 text-red-700 border-none" : "bg-slate-100 text-slate-700 border-none"}>
                    {sc.status || "Active"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(sc)}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(sc.id)}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* — Suppliers — */
function SuppliersTable({ companyId, masterSuppliers, cur, blockMessages }: { companyId: string; masterSuppliers: MasterSupplier[]; cur: string; blockMessages: Map<string, string> }) {
  const createSupplier = useCreateMasterSupplier();
  const updateSupplier = useUpdateMasterSupplier();
  const deleteSupplier = useDeleteMasterSupplier();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<MasterSupplier | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/master-suppliers"] });
    queryClient.invalidateQueries({ queryKey: ["/api/suppliers"] });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      name:        fd.get("name")        as string,
      cat:         fd.get("cat")         as string,
      contact:     fd.get("contact")     as string,
      phone:       fd.get("phone")       as string,
      email:       fd.get("email")       as string,
      vatNo:       fd.get("vatNo")       as string,
      creditLimit: (fd.get("creditLimit") as string) || "0",
      terms:       Number(fd.get("terms") || 30),
      status:      fd.get("status")      as string || "Active",
    };
    if (editing) {
      await updateSupplier.mutateAsync({ id: editing.id, data });
    } else {
      await createSupplier.mutateAsync({ data: { id: `MSP-${uid()}`, companyId, ...data } });
    }
    invalidate();
    setIsOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    const blockMsg = blockMessages.get(id);
    if (blockMsg) {
      await confirm({ title: "Cannot Delete Supplier", message: `This supplier cannot be deleted — it is ${blockMsg}. Remove all project links first.`, confirmLabel: "OK", hideCancelButton: true, destructive: false });
      return;
    }
    if (!await confirm({ title: 'Delete Supplier', message: 'Delete this supplier? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await deleteSupplier.mutateAsync({ id });
      invalidate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
      toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
    }
  };

  const openEdit = (s: MasterSupplier) => { setEditing(s); setIsOpen(true); };
  const openNew  = ()                  => { setEditing(null); setIsOpen(true); };

  const SUP_CATS = [
    "Cement & Concrete","Steel & Rebar","Timber & Formwork","Aggregates & Sand",
    "Waterproofing & Insulation","Paint & Finishes","Electrical Materials","Plumbing & Drainage",
    "HVAC Equipment","Heavy Plant & Equipment","Safety Equipment","Fuel & Lubricants",
    "IT & Communication","Professional Services","Other",
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> + Add Supplier</Button>
      </div>
      <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-full sm:max-w-[550px] rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Supplier / Vendor Name *</Label>
                <Input name="name" required defaultValue={editing?.name} placeholder="Supplier company name" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Category</Label>
                <Select name="cat" defaultValue={editing?.cat || ""}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {SUP_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Contact Person</Label>
                <Input name="contact" defaultValue={editing?.contact} placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <PhoneInput name="phone" defaultValue={editing?.phone} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input name="email" type="email" defaultValue={editing?.email} placeholder="contact@supplier.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Tax / VAT Number</Label>
                <Input name="vatNo" defaultValue={editing?.vatNo} placeholder="Registration number" />
              </div>
              <div className="space-y-1.5">
                <Label>Credit Limit ({cur})</Label>
                <Input name="creditLimit" type="number" min="0" defaultValue={editing?.creditLimit} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label>Payment Terms (days)</Label>
                <Input name="terms" type="number" min="0" defaultValue={editing?.terms} placeholder="30" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select name="status" defaultValue={editing?.status || "Active"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="Blacklisted">Blacklisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl">
              {editing ? "Save Changes" : "Add Supplier"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="rounded-2xl border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-24">ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>VAT No.</TableHead>
              <TableHead className="text-right">Credit Limit</TableHead>
              <TableHead className="text-right">Terms</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {masterSuppliers.length === 0 && (
              <TableRow><TableCell colSpan={11} className="text-center py-12 text-muted-foreground">No suppliers registered.</TableCell></TableRow>
            )}
            {masterSuppliers.map(s => (
              <TableRow key={s.id}>
                <TableCell className="text-xs text-muted-foreground font-mono">{s.id}</TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.cat || "—"}</TableCell>
                <TableCell>{s.contact || "—"}</TableCell>
                <TableCell>{s.phone || "—"}</TableCell>
                <TableCell className="text-primary text-sm">{s.email || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.vatNo || "—"}</TableCell>
                <TableCell className="text-right font-medium">{s.creditLimit ? fc(s.creditLimit, cur) : "—"}</TableCell>
                <TableCell className="text-right text-sm">{s.terms ? `${s.terms}d` : "—"}</TableCell>
                <TableCell>
                  <Badge className={s.status === "Active" ? "bg-emerald-100 text-emerald-700 border-none" : "bg-slate-100 text-slate-700 border-none"}>
                    {s.status || "Active"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(s.id)}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* — Staff — */
function StaffTable({ companyId, masterStaff, cur, blockMessages }: { companyId: string; masterStaff: MasterStaffMember[]; cur: string; blockMessages: Map<string, string> }) {
  const createStaff = useCreateMasterStaffMember();
  const updateStaff = useUpdateMasterStaffMember();
  const deleteStaff = useDeleteMasterStaffMember();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<MasterStaffMember | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/master-staff"] });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      name:     fd.get("name")     as string,
      jobTitle: fd.get("jobTitle") as string,
      dept:     fd.get("dept")     as string,
      basic:    fd.get("basic")    as string,
      house:    fd.get("house")    as string,
      trans:    fd.get("trans")    as string,
      idNo:     fd.get("idNo")     as string,
      status:   fd.get("status")   as string || "Active",
    };
    if (editing) {
      await updateStaff.mutateAsync({ id: editing.id, data });
    } else {
      await createStaff.mutateAsync({ data: { id: `MST-${uid()}`, companyId, ...data } });
    }
    invalidate();
    setIsOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    const blockMsg = blockMessages.get(id);
    if (blockMsg) {
      await confirm({ title: "Cannot Delete Staff Member", message: `This staff member cannot be deleted — they have ${blockMsg}. Remove all payroll records first.`, confirmLabel: "OK", hideCancelButton: true, destructive: false });
      return;
    }
    if (!await confirm({ title: 'Delete Staff Member', message: 'Delete this staff member? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await deleteStaff.mutateAsync({ id });
      invalidate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
      toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
    }
  };

  const openEdit = (s: MasterStaffMember) => { setEditing(s); setIsOpen(true); };
  const openNew  = ()                     => { setEditing(null); setIsOpen(true); };

  const DEPTS = ["Projects","Finance","Administration","HR","Procurement","Safety","IT","Operations","Engineering","Other"];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> + Add Staff</Button>
      </div>
      <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-full sm:max-w-[550px] rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Full Name *</Label>
                <Input name="name" required defaultValue={editing?.name} placeholder="Employee full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Job Title</Label>
                <Input name="jobTitle" defaultValue={editing?.jobTitle} placeholder="e.g. Project Manager" />
              </div>
              <div className="space-y-1.5">
                <Label>Department</Label>
                <Select name="dept" defaultValue={editing?.dept || ""}>
                  <SelectTrigger><SelectValue placeholder="Select dept." /></SelectTrigger>
                  <SelectContent>
                    {DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Basic Salary ({cur})</Label>
                <Input name="basic" type="number" min="0" defaultValue={editing?.basic} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label>Housing Allowance ({cur})</Label>
                <Input name="house" type="number" min="0" defaultValue={editing?.house} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label>Transport Allowance ({cur})</Label>
                <Input name="trans" type="number" min="0" defaultValue={editing?.trans} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label>National ID / Passport No.</Label>
                <Input name="idNo" defaultValue={editing?.idNo} placeholder="ID or passport number" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select name="status" defaultValue={editing?.status || "Active"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="On Leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl">
              {editing ? "Save Changes" : "Add Staff Member"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="rounded-2xl border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-24">ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Job Title</TableHead>
              <TableHead>Department</TableHead>
              <TableHead className="text-right">Basic Salary</TableHead>
              <TableHead className="text-right">Housing</TableHead>
              <TableHead className="text-right">Transport</TableHead>
              <TableHead>ID Number</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {masterStaff.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No staff members registered.</TableCell></TableRow>
            )}
            {masterStaff.map(s => (
              <TableRow key={s.id}>
                <TableCell className="text-xs text-muted-foreground font-mono">{s.id}</TableCell>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.jobTitle || "—"}</TableCell>
                <TableCell>
                  {s.dept && <Badge variant="outline" className="text-xs">{s.dept}</Badge>}
                </TableCell>
                <TableCell className="text-right font-medium">{s.basic ? fc(s.basic, cur) : "—"}</TableCell>
                <TableCell className="text-right text-sm">{s.house ? fc(s.house, cur) : "—"}</TableCell>
                <TableCell className="text-right text-sm">{s.trans ? fc(s.trans, cur) : "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.idNo || "—"}</TableCell>
                <TableCell>
                  <Badge className={s.status === "Active" ? "bg-emerald-100 text-emerald-700 border-none" : s.status === "On Leave" ? "bg-amber-100 text-amber-700 border-none" : "bg-slate-100 text-slate-700 border-none"}>
                    {s.status || "Active"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(s.id)}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* — Expense Categories — */
function ExpenseCategoriesTable({ companyId, expenseCategories, blockMessages }: { companyId: string; expenseCategories: ExpenseCategory[]; blockMessages: Map<string, string> }) {
  const createCategory = useCreateExpenseCategory();
  const updateCategory = useUpdateExpenseCategory();
  const deleteCategory = useDeleteExpenseCategory();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);
  const [search, setSearch] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getListExpenseCategoriesQueryKey({ companyId }) });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      name:        fd.get("name")        as string,
      description: fd.get("description") as string || "",
    };
    if (editing) {
      await updateCategory.mutateAsync({ id: editing.id, data });
    } else {
      await createCategory.mutateAsync({ data: { id: `EXC-${uid()}`, companyId, ...data } });
    }
    invalidate();
    setIsOpen(false);
    setEditing(null);
  };

  const { toast } = useToast();

  const handleDelete = async (id: string) => {
    const blockMsg = blockMessages.get(id);
    if (blockMsg) {
      await confirm({ title: "Cannot Delete Category", message: `This expense category cannot be deleted — it is ${blockMsg}. Remove those budget items first.`, confirmLabel: "OK", hideCancelButton: true, destructive: false });
      return;
    }
    if (!await confirm({ title: 'Delete Expense Category', message: 'Delete this expense category? This action cannot be undone.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await deleteCategory.mutateAsync({ id });
      invalidate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Delete failed.";
      toast({ title: "Cannot Delete", description: msg, variant: "destructive" });
    }
  };

  const openEdit = (c: ExpenseCategory) => { setEditing(c); setIsOpen(true); };
  const openNew  = ()                    => { setEditing(null); setIsOpen(true); };

  const filtered = search
    ? expenseCategories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.description ?? "").toLowerCase().includes(search.toLowerCase()))
    : expenseCategories;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Input
          placeholder="Search categories…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Button onClick={openNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> + Add Category</Button>
      </div>

      <Dialog open={isOpen} onOpenChange={v => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-full sm:max-w-[480px] rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Expense Category" : "Add Expense Category"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label>Category Name *</Label>
              <Input name="name" required defaultValue={editing?.name} placeholder="e.g. Site Accommodation" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input name="description" defaultValue={editing?.description} placeholder="Optional description" />
            </div>
            <Button type="submit" className="w-full h-10 rounded-xl">
              {editing ? "Save Changes" : "Add Category"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="rounded-2xl border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Category Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-12 text-muted-foreground">
                  {expenseCategories.length === 0
                    ? "No expense categories yet. Add your first category above."
                    : "No categories match your search."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{c.description || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(c.id)}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ TEAM */
function TeamTab({ companyId }: { companyId: string }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Viewer");
  const queryClient = useQueryClient();

  const { data: members = [], isLoading: membersLoading } = useListMembers();
  const { data: allInvitations = [], isLoading: invitationsLoading } = useListInvitations();
  const sendInvite = useSendInvite();
  const loading = membersLoading || invitationsLoading;
  const invitations = allInvitations.filter((i: InvitationItem) => i.status === "Pending");

  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    try {
      const result = await sendInvite.mutateAsync({ data: { email: inviteEmail, role: inviteRole, companyId } }) as { emailSent?: boolean; inviteToken?: string } | undefined;
      setInviteEmail("");
      queryClient.invalidateQueries({ queryKey: ["/api/auth/members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/invitations"] });
      if (result && result.emailSent === false) {
        setInviteNote(`Invitation recorded (email not sent — no email provider configured). Share this invite token manually: ${result.inviteToken ?? ''}`);
      } else {
        setInviteNote(null);
      }
    } catch (err: unknown) {
      console.error("Invite failed:", err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Team</h2>

      <Card className="p-6 rounded-2xl border-border/50">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Invite Team Member</h3>
        <form onSubmit={handleInvite} className="flex gap-3 flex-wrap">
          <Input
            type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
            placeholder="colleague@company.com" required className="flex-1 min-w-[220px]"
          />
          <Select value={inviteRole} onValueChange={setInviteRole}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Admin">Admin</SelectItem>
              <SelectItem value="Project Manager">Project Manager</SelectItem>
              <SelectItem value="Finance Manager">Finance Manager</SelectItem>
              <SelectItem value="Accountant">Accountant</SelectItem>
              <SelectItem value="Viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={sendInvite.isPending}>
            {sendInvite.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Send Invite
          </Button>
        </form>
        {inviteNote && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm break-all">
            {inviteNote}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl border-border/50 overflow-hidden">
        <div className="p-4 border-b border-border/50 flex items-center justify-between">
          <h3 className="font-semibold">Team Members ({members.length})</h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 && !loading && (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No team members found.</TableCell></TableRow>
            )}
            {(members as MemberItem[]).map((m: MemberItem) => (
              <TableRow key={m.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                      {m.name?.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium">{m.name}</div>
                      {m.isOwner && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Owner</span>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{m.email}</TableCell>
                <TableCell><Badge variant="outline">{m.role}</Badge></TableCell>
                <TableCell><Badge className="bg-emerald-100 text-emerald-700 border-none">{m.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {invitations.length > 0 && (
        <Card className="rounded-2xl border-border/50 overflow-hidden">
          <div className="p-4 border-b border-border/50">
            <h3 className="font-semibold">Pending Invitations ({invitations.length})</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((inv: InvitationItem) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-medium">{inv.email}</TableCell>
                  <TableCell>{inv.role}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{inv.companyName || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-amber-600 text-sm font-medium">Pending</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ TAX MANAGER */
function TaxManager({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: taxTypes = [], isLoading } = useListTaxTypes({ companyId });
  const createType = useCreateTaxType();
  const updateType = useUpdateTaxType();
  const deleteType = useDeleteTaxType();
  const createRate = useCreateTaxRate();
  const updateRate = useUpdateTaxRate();
  const deleteRate = useDeleteTaxRate();

  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});
  const [addingTypeName, setAddingTypeName] = useState("");
  const [showAddType, setShowAddType] = useState(false);
  const [editingType, setEditingType] = useState<{ id: string; name: string } | null>(null);
  const [addingRate, setAddingRate] = useState<{ typeId: string; label: string; rate: string } | null>(null);
  const [editingRate, setEditingRate] = useState<{ id: string; typeId: string; label: string; rate: string } | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getListTaxTypesQueryKey({ companyId }) });

  const handleAddType = async () => {
    if (!addingTypeName.trim()) return;
    await createType.mutateAsync({ data: { id: `TT-${uid()}`, companyId, name: addingTypeName.trim(), order: taxTypes.length } });
    setAddingTypeName("");
    setShowAddType(false);
    invalidate();
  };

  const handleUpdateType = async (id: string, name: string) => {
    await updateType.mutateAsync({ id, data: { name } });
    setEditingType(null);
    invalidate();
  };

  const handleDeleteType = async (t: TaxTypeWithRates) => {
    const ok = await confirm({ title: "Delete Tax Type", description: `Delete "${t.name}" and all its rates? This cannot be undone.` });
    if (!ok) return;
    await deleteType.mutateAsync({ id: t.id });
    invalidate();
  };

  const handleAddRate = async (typeId: string) => {
    if (!addingRate || !addingRate.label.trim()) return;
    await createRate.mutateAsync({ data: { id: `TR-${uid()}`, taxTypeId: typeId, companyId, label: addingRate.label.trim(), rate: String(N(addingRate.rate) / 100), isDefault: false } });
    setAddingRate(null);
    invalidate();
  };

  const handleUpdateRate = async (id: string) => {
    if (!editingRate) return;
    await updateRate.mutateAsync({ id, data: { label: editingRate.label.trim(), rate: String(N(editingRate.rate) / 100) } });
    setEditingRate(null);
    invalidate();
  };

  const handleToggleDefault = async (rate: TaxRate, taxTypeId: string) => {
    await updateRate.mutateAsync({ id: rate.id, data: { isDefault: !rate.isDefault } });
    if (!rate.isDefault) {
      const siblings = taxTypes.find(t => t.id === taxTypeId)?.rates ?? [];
      for (const r of siblings) {
        if (r.id !== rate.id && r.isDefault) {
          await updateRate.mutateAsync({ id: r.id, data: { isDefault: false } });
        }
      }
    }
    invalidate();
  };

  const handleDeleteRate = async (rate: TaxRate) => {
    const ok = await confirm({ title: "Delete Rate", description: `Delete "${rate.label}" rate? This cannot be undone.` });
    if (!ok) return;
    await deleteRate.mutateAsync({ id: rate.id });
    invalidate();
  };

  const toggleExpand = (id: string) => setExpandedTypes(prev => ({ ...prev, [id]: !prev[id] }));

  if (isLoading) return <div className="py-4 text-sm text-muted-foreground">Loading tax configuration…</div>;

  return (
    <div className="space-y-3">
      {taxTypes.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">No tax types configured yet. Add your first tax type below.</p>
      )}
      {taxTypes.map((taxType) => {
        const isExpanded = expandedTypes[taxType.id] !== false;
        return (
          <div key={taxType.id} className="border border-border/60 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors">
              <button type="button" onClick={() => toggleExpand(taxType.id)} className="flex items-center gap-2 flex-1 text-left">
                {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                {editingType?.id === taxType.id ? (
                  <Input
                    autoFocus
                    value={editingType.name}
                    onChange={e => setEditingType({ ...editingType, name: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUpdateType(taxType.id, editingType.name); } if (e.key === 'Escape') setEditingType(null); }}
                    className="h-7 text-sm font-semibold rounded-lg max-w-xs"
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span className="font-semibold text-sm">{taxType.name}</span>
                )}
                <span className="text-xs text-muted-foreground ml-1">({taxType.rates.length} rate{taxType.rates.length !== 1 ? 's' : ''})</span>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                {editingType?.id === taxType.id ? (
                  <>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleUpdateType(taxType.id, editingType.name)}>Save</Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingType(null)}>Cancel</Button>
                  </>
                ) : (
                  <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingType({ id: taxType.id, name: taxType.name })}><Pencil className="w-3.5 h-3.5" /></Button>
                )}
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteType(taxType)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            {isExpanded && (
              <div className="px-4 pb-3 pt-2 space-y-2">
                {taxType.rates.length === 0 && (
                  <p className="text-xs text-muted-foreground py-1">No rates yet. Add a sub-rate below.</p>
                )}
                {taxType.rates.map((rate) => (
                  <div key={rate.id} className="flex items-center gap-2 py-1.5 border-b border-border/30 last:border-0">
                    {editingRate?.id === rate.id ? (
                      <>
                        <Input autoFocus value={editingRate.label} onChange={e => setEditingRate({ ...editingRate, label: e.target.value })} className="h-7 text-xs rounded-lg flex-1" placeholder="Label" />
                        <div className="flex items-center gap-1">
                          <Input value={editingRate.rate} onChange={e => setEditingRate({ ...editingRate, rate: e.target.value })} type="number" min="0" max="100" step="0.01" className="h-7 text-xs rounded-lg w-20" placeholder="%" />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleUpdateRate(rate.id)}>Save</Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingRate(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{rate.label}</span>
                        <span className="text-sm text-muted-foreground">{(N(rate.rate) * 100).toFixed(2).replace(/\.00$/, '')}%</span>
                        <button
                          type="button"
                          title={rate.isDefault ? "Default rate" : "Set as default"}
                          onClick={() => handleToggleDefault(rate, taxType.id)}
                          className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${rate.isDefault ? 'text-amber-500' : 'text-muted-foreground/30 hover:text-muted-foreground'}`}
                        >
                          <Star className={`w-3.5 h-3.5 ${rate.isDefault ? 'fill-amber-500' : ''}`} />
                        </button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingRate({ id: rate.id, typeId: taxType.id, label: rate.label, rate: String((N(rate.rate) * 100).toFixed(4)) })}><Pencil className="w-3 h-3" /></Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteRate(rate)}><Trash2 className="w-3 h-3" /></Button>
                      </>
                    )}
                  </div>
                ))}
                {addingRate?.typeId === taxType.id ? (
                  <div className="flex items-center gap-2 pt-1">
                    <Input autoFocus value={addingRate.label} onChange={e => setAddingRate({ ...addingRate, label: e.target.value })} className="h-7 text-xs rounded-lg flex-1" placeholder="e.g. Standard" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRate(taxType.id); } if (e.key === 'Escape') setAddingRate(null); }} />
                    <div className="flex items-center gap-1">
                      <Input value={addingRate.rate} onChange={e => setAddingRate({ ...addingRate, rate: e.target.value })} type="number" min="0" max="100" step="0.01" className="h-7 text-xs rounded-lg w-20" placeholder="%" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleAddRate(taxType.id)}>Add</Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAddingRate(null)}>Cancel</Button>
                  </div>
                ) : (
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1.5 mt-1" onClick={() => setAddingRate({ typeId: taxType.id, label: "", rate: "" })}>
                    <Plus className="w-3 h-3" /> Add Rate
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {showAddType ? (
        <div className="flex items-center gap-2">
          <Input autoFocus value={addingTypeName} onChange={e => setAddingTypeName(e.target.value)} placeholder="e.g. VAT, GST, Withholding Tax" className="rounded-xl h-9 text-sm flex-1" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddType(); } if (e.key === 'Escape') { setShowAddType(false); setAddingTypeName(""); } }} />
          <Button type="button" size="sm" onClick={handleAddType} disabled={!addingTypeName.trim() || createType.isPending}>Add</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => { setShowAddType(false); setAddingTypeName(""); }}>Cancel</Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" className="rounded-xl gap-2" onClick={() => setShowAddType(true)}>
          <Plus className="w-4 h-4" /> Add Tax Type
        </Button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════ PAYROLL TAX MANAGER */
const PAYROLL_TAX_TYPES = [
  { value: "employee", label: "Employee Deduction" },
  { value: "employer", label: "Employer Contribution" },
  { value: "both",     label: "Both (Employee + Employer)" },
];

function PayrollTaxManager({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data: configs = [], isLoading } = useListPayrollTaxConfigs({ companyId });
  const createConfig = useCreatePayrollTaxConfig();
  const updateConfig = useUpdatePayrollTaxConfig();
  const deleteConfig = useDeletePayrollTaxConfig();

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", type: "employee", rate: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: "", type: "employee", rate: "" });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getListPayrollTaxConfigsQueryKey({ companyId }) });

  const handleAdd = async () => {
    if (!addForm.name.trim() || !addForm.rate) return;
    await createConfig.mutateAsync({
      data: {
        id: `PTC-${uid()}`,
        companyId,
        name: addForm.name.trim(),
        type: addForm.type,
        rate: String(N(addForm.rate) / 100),
        isActive: true,
        order: configs.length,
      },
    });
    setAddForm({ name: "", type: "employee", rate: "" });
    setShowAdd(false);
    invalidate();
  };

  const startEdit = (c: PayrollTaxConfig) => {
    setEditingId(c.id);
    setEditForm({ name: c.name, type: c.type, rate: String(N(c.rate) * 100) });
  };

  const handleUpdate = async (id: string) => {
    await updateConfig.mutateAsync({ id, data: { name: editForm.name, type: editForm.type, rate: String(N(editForm.rate) / 100) } });
    setEditingId(null);
    invalidate();
  };

  const handleToggleActive = async (c: PayrollTaxConfig) => {
    await updateConfig.mutateAsync({ id: c.id, data: { isActive: !c.isActive } });
    invalidate();
  };

  const handleDelete = async (c: PayrollTaxConfig) => {
    const ok = await confirm({ title: "Delete Payroll Tax", description: `Delete "${c.name}"? This cannot be undone.` });
    if (!ok) return;
    await deleteConfig.mutateAsync({ id: c.id });
    invalidate();
  };

  if (isLoading) return <div className="py-4 text-sm text-muted-foreground">Loading payroll taxes…</div>;

  return (
    <div className="space-y-3">
      {configs.length === 0 && !showAdd && (
        <p className="text-sm text-muted-foreground py-2">No payroll taxes configured yet. Add your first one below.</p>
      )}
      {configs.map(c => (
        <div key={c.id} className={`flex items-center gap-3 px-4 py-3 border rounded-xl transition-colors ${c.isActive ? "border-border/60 bg-card" : "border-border/30 bg-muted/30 opacity-60"}`}>
          {editingId === c.id ? (
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input autoFocus value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => { if (e.key === "Enter") handleUpdate(c.id); if (e.key === "Escape") setEditingId(null); }}
                className="h-8 text-sm rounded-lg" placeholder="Tax name" />
              <Select value={editForm.type} onValueChange={v => setEditForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="h-8 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYROLL_TAX_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="100" step="0.01" value={editForm.rate}
                  onChange={e => setEditForm(p => ({ ...p, rate: e.target.value }))}
                  className="h-8 text-sm rounded-lg" placeholder="Rate %" />
                <span className="text-sm text-muted-foreground">%</span>
                <Button type="button" size="sm" variant="ghost" onClick={() => handleUpdate(c.id)} className="h-8 px-2 text-emerald-600 hover:text-emerald-700"><CheckCircle2 className="w-4 h-4" /></Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8 px-2"><X className="w-4 h-4" /></Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{c.name}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">
                    {PAYROLL_TAX_TYPES.find(t => t.value === c.type)?.label ?? c.type}
                  </span>
                  {!c.isActive && <span className="text-xs text-muted-foreground">(inactive)</span>}
                </div>
              </div>
              <span className="font-semibold text-sm tabular-nums">{fp(N(c.rate))}%</span>
              <div className="flex items-center gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => handleToggleActive(c)} className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
                  {c.isActive ? "Disable" : "Enable"}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => startEdit(c)} className="h-7 px-2"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => handleDelete(c)} className="h-7 px-2 text-rose-500 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </>
          )}
        </div>
      ))}

      {showAdd ? (
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="space-y-1">
              <Label className="text-xs">Tax / Deduction Name *</Label>
              <Input autoFocus value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") setShowAdd(false); }}
                placeholder="e.g. Income Tax (PAYE)" className="h-9 rounded-lg text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Applies To</Label>
              <Select value={addForm.type} onValueChange={v => setAddForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYROLL_TAX_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rate (%)</Label>
              <div className="relative">
                <Input type="number" min="0" max="100" step="0.01" value={addForm.rate}
                  onChange={e => setAddForm(p => ({ ...p, rate: e.target.value }))}
                  placeholder="e.g. 5" className="h-9 rounded-lg text-sm pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={handleAdd} disabled={!addForm.name.trim() || !addForm.rate} className="rounded-lg">Add Tax</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setShowAdd(false); setAddForm({ name: "", type: "employee", rate: "" }); }} className="rounded-lg">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button type="button" size="sm" variant="outline" onClick={() => setShowAdd(true)} className="rounded-lg">
          <Plus className="w-4 h-4 mr-1.5" /> Add Payroll Tax
        </Button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ COMPANY SETTINGS */
function SettingsTab({ companyId, company }: { companyId: string; company: Company }) {
  const updateCompany = useUpdateCompany();
  const queryClient   = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [color,  setColor]  = useState(company?.color || "#1a2332");
  const [planChangeTarget, setPlanChangeTarget] = useState<PlanDef | null>(null);
  const [planChanging, setPlanChanging] = useState(false);
  const { toast } = useToast();

  // Proration helper — days left in current billing month
  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - today.getDate() + 1;
  const nextRenewal = new Date(today.getFullYear(), today.getMonth() + 1, 1)
    .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const realPlanDef = getPlan(company?.plan);

  const getProratedAmount = (newPlan: PlanDef): number => {
    const dailyDiff = (newPlan.priceMonthly - realPlanDef.priceMonthly) / daysInMonth;
    return Math.max(0, parseFloat((dailyDiff * daysRemaining).toFixed(2)));
  };

  const handlePlanChange = async () => {
    if (!planChangeTarget) return;
    setPlanChanging(true);
    try {
      await updateCompany.mutateAsync({
        id: companyId,
        data: { plan: planChangeTarget.id } as any,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      toast({
        title: "Plan updated",
        description: `Your plan has been changed to ${planChangeTarget.label}.`,
      });
      setPlanChangeTarget(null);
    } catch {
      toast({ title: "Error", description: "Failed to update plan. Please try again.", variant: "destructive" });
    } finally {
      setPlanChanging(false);
    }
  };
  const { data: stUser } = useGetMe();
  const { profile: stDemoProfile, getEffectiveFeatures: stGetEff } = useDemoProfile();
  const stIsDemo = !!(stUser?.company?.isDemo);
  const stIsAdmin = stUser?.role === "Admin" || stUser?.role === "Super Admin";

  const settingsInitialInnerTab = (() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      if (!stIsDemo && (p.get("integration_connected") || p.get("integration_error"))) return "integrations";
    }
    return "profile";
  })();
  const settingsEffectivePlan = (stIsDemo && stDemoProfile?.planId) ? stDemoProfile.planId : (company?.plan ?? "pro");
  const settingsFeat = stGetEff(settingsEffectivePlan);

  const [sf, setSf] = useState({
    name:           company?.name           ?? "",
    industry:       company?.industry       ?? "Construction & Civil Engineering",
    country:        company?.country        ?? "",
    founded:        company?.founded        ?? "",
    employees:      company?.employees      ?? "",
    website:        company?.website        ?? "",
    address:        company?.address        ?? "",
    contact:        company?.contact        ?? "",
    phone:          company?.phone          ?? "",
    crNo:           company?.crNo           ?? "",
    vatNo:          company?.vatNo          ?? "",
    currency:       company?.currency       ?? "USD",
    taxLabel:       company?.taxLabel       ?? "VAT",
    taxRate:        String(N(company?.taxRate) * 100),
    logoUrl:        (company as any)?.logoUrl       ?? "",
    tagline:        (company as any)?.tagline       ?? "",
    docHeaderStyle: (company as any)?.docHeaderStyle ?? "standard",
  });
  const setF = (k: keyof typeof sf) => (v: string) => setSf(prev => ({ ...prev, [k]: v }));

  const BRAND_COLORS = ["#1a2332","#1a5276","#1e8449","#6c3483","#922b21","#1a5e20","#154360","#7d6608","#1b2631","#212f3d"];

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const taxRateRaw = N(sf.taxRate) / 100;
    setSaving(true);
    try {
      await updateCompany.mutateAsync({
        id: companyId,
        data: {
          name:           sf.name,
          industry:       sf.industry,
          country:        sf.country,
          founded:        sf.founded,
          employees:      sf.employees,
          website:        sf.website,
          address:        sf.address,
          contact:        sf.contact,
          phone:          sf.phone,
          crNo:           sf.crNo,
          vatNo:          sf.vatNo,
          currency:       sf.currency,
          currencySymbol: getCurrencySymbol(sf.currency),
          taxRate:        String(taxRateRaw),
          taxLabel:       sf.taxLabel,
          color,
          ...(settingsFeat.printExport ? { logoUrl: sf.logoUrl } : {}),
          ...(settingsFeat.customBranding ? {
            tagline:        sf.tagline,
            docHeaderStyle: sf.docHeaderStyle,
          } : {}),
        } as any,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Company Settings</h2>
          <p className="text-muted-foreground text-sm mt-0.5">Manage your company profile, branding, currency and preferences</p>
        </div>
        <Button form="settingsForm" type="submit" disabled={saving} className="rounded-xl min-w-32">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {saved ? <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" /> Saved!</> : "Save Settings"}
        </Button>
      </div>

      <form id="settingsForm" onSubmit={handleSave}>
        <Tabs defaultValue={settingsInitialInnerTab} className="space-y-5">
          <TabsList className="flex-wrap h-auto gap-1 bg-muted/60 p-1 rounded-xl">
            <TabsTrigger value="profile"      className="rounded-lg text-sm">Profile</TabsTrigger>
            <TabsTrigger value="contact"      className="rounded-lg text-sm">Contact & Legal</TabsTrigger>
            <TabsTrigger value="tax"          className="rounded-lg text-sm">Tax</TabsTrigger>
            <TabsTrigger value="payroll-taxes" className="rounded-lg text-sm flex items-center gap-1">
              Payroll Taxes {!settingsFeat.payroll && <Lock className="w-3 h-3 opacity-50" />}
            </TabsTrigger>
            <TabsTrigger value="currency"      className="rounded-lg text-sm">Currency</TabsTrigger>
            <TabsTrigger value="branding"      className="rounded-lg text-sm flex items-center gap-1">
              Branding {!settingsFeat.customBranding && <Lock className="w-3 h-3 opacity-50" />}
            </TabsTrigger>
            <TabsTrigger value="sequences"     className="rounded-lg text-sm flex items-center gap-1">
              Doc Sequences {!settingsFeat.docSequencing && <Lock className="w-3 h-3 opacity-50" />}
            </TabsTrigger>
            <TabsTrigger value="subscription"  className="rounded-lg text-sm">Subscription</TabsTrigger>
            <TabsTrigger
              value="integrations"
              className={`rounded-lg text-sm flex items-center gap-1 ${stIsDemo ? "opacity-50" : ""}`}
            >
              <Plug className="w-3.5 h-3.5" />
              Integrations
              {stIsDemo && <Lock className="w-3 h-3 opacity-70" />}
            </TabsTrigger>
          </TabsList>

          {/* ── Profile ── */}
          <TabsContent value="profile">
            <Card className="p-6 rounded-2xl border-border/50">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-5">Company Profile</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Company Name *</Label>
                  <Input name="name" required value={sf.name} onChange={e => setF("name")(e.target.value)} placeholder="Your company name" />
                </div>
                {company?.slug && (
                  <div className="md:col-span-2 space-y-1.5">
                    <Label>Company URL Slug</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-10 flex items-center px-3 bg-muted/50 rounded-lg text-sm font-mono text-muted-foreground border border-border/50">
                        /app/<span className="text-foreground font-semibold">{company.slug}</span>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">Read-only</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Your workspace URL path. Slugs are auto-generated from the company name.</p>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Industry</Label>
                  <Select name="industry" value={sf.industry} onValueChange={setF("industry")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Select name="country" value={sf.country} onValueChange={setF("country")}>
                    <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Founded Year</Label>
                  <Input name="founded" value={sf.founded} onChange={e => setF("founded")(e.target.value)} placeholder="e.g. 2010" />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Size</Label>
                  <Select name="employees" value={sf.employees} onValueChange={setF("employees")}>
                    <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      {["1-10","11-50","51-100","100-250","250-500","500-1000","1000+"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Website</Label>
                  <Input name="website" value={sf.website} onChange={e => setF("website")(e.target.value)} placeholder="www.yourcompany.com" />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Head Office Address</Label>
                  <Input name="address" value={sf.address} onChange={e => setF("address")(e.target.value)} placeholder="Street address, City, Country" />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ── Contact & Legal ── */}
          <TabsContent value="contact">
            <Card className="p-6 rounded-2xl border-border/50">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-5">Contact & Legal</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label>Primary Contact</Label>
                  <Input name="contact" value={sf.contact} onChange={e => setF("contact")(e.target.value)} placeholder="admin@company.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <PhoneInput name="phone" defaultValue={company?.phone} country={sf.country} onChange={setF("phone")} />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Registration No.</Label>
                  <Input name="crNo" value={sf.crNo} onChange={e => setF("crNo")(e.target.value)} placeholder="Business registration number" />
                </div>
                <div className="space-y-1.5">
                  <Label>Tax Registration Number</Label>
                  <Input name="vatNo" value={sf.vatNo} onChange={e => setF("vatNo")(e.target.value)} placeholder="Tax ID / VAT / GST number" />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ── Tax ── */}
          <TabsContent value="tax" className="space-y-5">
            {/* Standard tax settings — available on all plans */}
            <Card className="p-6 rounded-2xl border-border/50">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-1">Tax Settings</h3>
              <p className="text-sm text-muted-foreground mb-5">Set the default tax label and rate applied across invoices and documents.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <Label>Tax Type Label</Label>
                  <Select name="taxLabel" value={sf.taxLabel} onValueChange={setF("taxLabel")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TAX_LABELS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Default Rate (%)</Label>
                  <Input name="taxRate" type="number" min="0" max="100" step="0.01" value={sf.taxRate} onChange={e => setF("taxRate")(e.target.value)} placeholder="e.g. 15" />
                </div>
                <div className="space-y-1.5">
                  <Label>Preview</Label>
                  <div className="h-10 flex items-center px-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                    {sf.taxLabel || "VAT"} @ {sf.taxRate || 0}% applied to invoices
                  </div>
                </div>
              </div>
            </Card>

            {/* Advanced tax configuration — gated on taxConfig (Pro+) */}
            <Card className="p-6 rounded-2xl border-border/50">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-2">
                Advanced Tax Configuration
                {!settingsFeat.taxConfig && <Lock className="w-3.5 h-3.5 opacity-40" />}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                Define multiple tax types (e.g. VAT, GST, Withholding) with sub-rates (e.g. Standard 20%, Reduced 5%). These appear as a structured picker across all transaction forms.
                {settingsFeat.taxConfig && <span className="ml-1 inline-flex items-center gap-1 text-amber-600"><Star className="w-3 h-3 fill-amber-500" /> = default rate for that type.</span>}
              </p>
              {!settingsFeat.taxConfig ? (
                <UpgradeTeaser feature="Advanced Tax Configuration" requiredPlan="Pro or higher (Pro / Pro Plus)" />
              ) : (
                <>
                  {sf.taxLabel !== (company?.taxLabel ?? "VAT") && (
                    <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
                      <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                      <span>
                        You changed the tax label from <strong>{company?.taxLabel ?? "VAT"}</strong> to <strong>{sf.taxLabel}</strong>. The structured tax types below may still use the old name — review them to avoid confusion.
                      </span>
                    </div>
                  )}
                  <TaxManager companyId={companyId} />
                </>
              )}
            </Card>
          </TabsContent>

          {/* ── Payroll Taxes ── */}
          <TabsContent value="payroll-taxes" className="space-y-5">
            {!settingsFeat.payroll ? (
              <UpgradeTeaser feature="Payroll Tax & Deduction Rules" requiredPlan="Lite or higher" />
            ) : (
              <Card className="p-6 rounded-2xl border-border/50">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-1">Payroll Tax & Deduction Rules</h3>
                <p className="text-sm text-muted-foreground mb-5">
                  Define the payroll taxes and statutory deductions applicable to your workforce (e.g. Income Tax, Social Security, Pension). These rates are used to auto-calculate employee deductions in payroll entries.
                </p>
                <PayrollTaxManager companyId={companyId} />
              </Card>
            )}
          </TabsContent>

          {/* ── Currency ── */}
          <TabsContent value="currency">
            <Card className="p-6 rounded-2xl border-border/50">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-5">Currency & Financials</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label>Primary Currency</Label>
                  <Select name="currency" value={sf.currency} onValueChange={setF("currency")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Preview</Label>
                  <div className="h-10 flex items-center px-3 bg-muted/50 rounded-lg text-sm font-medium">
                    {sf.currency || "USD"} 1,234,567 · {sf.currency || "USD"} 0.00
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ── Branding ── */}
          <TabsContent value="branding" className="space-y-5">
            {/* Company Logo — Solo, Contractor, Pro, Group plans */}
            {!settingsFeat.printExport ? (
              <UpgradeTeaser feature="Company Logo on Documents" requiredPlan="Lite or higher" />
            ) : (
              <Card className="p-6 rounded-2xl border-border/50">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-5">Company Logo</h3>
                <div className="space-y-1.5 max-w-xl">
                  <Label>Logo URL</Label>
                  <Input
                    value={sf.logoUrl}
                    onChange={e => setF("logoUrl")(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    type="url"
                  />
                  <p className="text-xs text-muted-foreground">Paste a direct link to your PNG, JPG, or SVG logo. Recommended max width: 500 px. The logo will appear on all printed invoices, proposals, and purchase orders.</p>
                </div>
                {sf.logoUrl && (
                  <div className="mt-4 flex items-center gap-4 p-4 rounded-xl bg-muted/40 border border-border/50 max-w-xl">
                    <img
                      src={sf.logoUrl}
                      alt="Logo preview"
                      className="h-14 w-auto max-w-[180px] object-contain rounded"
                      onError={e => (e.currentTarget.style.display = "none")}
                    />
                    <div>
                      <div className="font-semibold text-sm">{company?.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Logo preview · will appear on printed documents</div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Advanced Branding — Group plan only */}
            {!settingsFeat.customBranding ? (
              <UpgradeTeaser feature="Custom Branding (tagline, accent colour, doc header style)" requiredPlan="Pro or higher" />
            ) : (<>
              {/* Identity */}
              <Card className="p-6 rounded-2xl border-border/50">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-5">Company Identity</h3>
                <div className="max-w-xl space-y-1.5">
                  <Label>Company Tagline / Slogan</Label>
                  <Input
                    value={sf.tagline}
                    onChange={e => setF("tagline")(e.target.value)}
                    placeholder="e.g. Building Excellence Since 2005"
                    maxLength={120}
                  />
                  <p className="text-xs text-muted-foreground">Appears on invoices, proposals, and document headers.</p>
                </div>
              </Card>

              {/* Brand colour */}
              <Card className="p-6 rounded-2xl border-border/50">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-5">Brand Colour</h3>
                <div className="space-y-5">
                  <div className="flex gap-2 flex-wrap">
                    {BRAND_COLORS.map(c => (
                      <button
                        key={c} type="button"
                        onClick={() => setColor(c)}
                        className={`w-10 h-10 rounded-xl transition-all ${color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : "hover:scale-105"}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">Custom colour</Label>
                      <div className="w-9 h-9 rounded-xl border border-border" style={{ backgroundColor: color }} />
                      <Input
                        type="text" value={color}
                        onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setColor(e.target.value); }}
                        className="w-28 font-mono text-sm"
                        placeholder="#1a2332"
                      />
                    </div>
                    <div className="flex items-center gap-3 ml-auto">
                      <span className="text-sm text-muted-foreground">Preview:</span>
                      <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-xl">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: color }}>
                          {company?.name?.charAt(0) ?? "F"}
                        </div>
                        <span className="font-semibold text-sm">{company?.name ?? "Your Company"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Document header style */}
              <Card className="p-6 rounded-2xl border-border/50">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Document Header Style</h3>
                <p className="text-sm text-muted-foreground mb-5">Choose how your company header appears on invoices, proposals, and purchase orders.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: "minimal",  label: "Minimal",  desc: "Company name only, clean and compact" },
                    { id: "standard", label: "Standard", desc: "Name, contact, address and tax info" },
                    { id: "branded",  label: "Branded",  desc: "Logo + colour accent + full details" },
                  ].map(s => (
                    <button
                      key={s.id} type="button"
                      onClick={() => setF("docHeaderStyle")(s.id)}
                      className={`p-4 rounded-xl border text-left transition-all ${sf.docHeaderStyle === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                    >
                      <div className="font-semibold text-sm mb-1 flex items-center gap-2">
                        {s.label}
                        {sf.docHeaderStyle === s.id && <CheckCircle2 className="w-3.5 h-3.5 text-primary" />}
                      </div>
                      <div className="text-xs text-muted-foreground">{s.desc}</div>
                      {/* Mini doc preview */}
                      <div className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-2 space-y-1">
                        {s.id !== "minimal" && sf.logoUrl && (
                          <div className="h-4 w-12 rounded-sm bg-muted" />
                        )}
                        <div className="h-2 w-24 rounded-sm" style={{ backgroundColor: s.id === "branded" ? color : "#d1d5db" }} />
                        {s.id !== "minimal" && <div className="h-1.5 w-16 rounded-sm bg-muted" />}
                        {s.id === "standard" || s.id === "branded" ? <div className="h-1.5 w-20 rounded-sm bg-muted" /> : null}
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            </>)}
          </TabsContent>

          {/* ── Doc Sequences ── */}
          <TabsContent value="sequences">
            {!settingsFeat.docSequencing ? (
              <UpgradeTeaser feature="Custom Document Numbering" requiredPlan="Lite or higher" />
            ) : (
              <DocSequencesTab companyId={companyId} />
            )}
          </TabsContent>

          <TabsContent value="subscription">
            <Card className="p-6 rounded-2xl border-border/50">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Subscription Plan</h3>
              <p className="text-sm text-muted-foreground mb-5">Your current plan determines which features are available across all projects and settings.</p>

              {/* Demo preview notice */}
              {stIsDemo && stDemoProfile?.planId && stDemoProfile.planId !== (company?.plan ?? "pro") && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-400/50 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
                  <Info className="w-4 h-4 shrink-0" />
                  <span>
                    You are previewing as <strong>{getPlan(stDemoProfile.planId).label}</strong> via the demo banner.
                    Your real plan is highlighted below.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {PLAN_DEFS.map(p => {
                  const isCurrent = realPlanDef.id === p.id;
                  const isUpgrade = p.priceMonthly > realPlanDef.priceMonthly;
                  const isDowngrade = p.priceMonthly < realPlanDef.priceMonthly;
                  const isDemo = stIsDemo && stDemoProfile?.planId === p.id && !isCurrent;
                  return (
                    <div
                      key={p.id}
                      className={`p-4 rounded-xl border relative flex flex-col ${
                        isCurrent
                          ? "border-primary bg-primary/5 shadow-sm"
                          : isDemo
                          ? "border-blue-400/60 bg-blue-50/30 dark:bg-blue-950/20"
                          : "border-border"
                      }`}
                    >
                      {p.popular && !isCurrent && !isDemo && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">Popular</div>
                      )}
                      {isDemo && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">Demo Preview</div>
                      )}
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className={`w-4 h-4 rounded-md ${p.color} flex-shrink-0`} />
                        <span className="font-bold text-sm">{p.label}</span>
                        {isCurrent && <CheckCircle2 className="w-3.5 h-3.5 text-primary ml-auto" />}
                      </div>
                      <div className="text-primary font-bold text-base mb-0.5">{p.price}</div>
                      <div className="text-[10px] text-muted-foreground mb-2">
                        {p.maxProjects === -1 ? "Unlimited" : p.maxProjects} projects · {p.maxUsers === -1 ? "Unlimited" : p.maxUsers} users
                      </div>
                      <ul className="space-y-1 mb-3 flex-1">
                        {p.highlights.slice(0, 4).map(f => (
                          <li key={f} className="text-[10px] text-muted-foreground flex items-start gap-1">
                            <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500 mt-0.5 shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <div className="mt-auto text-[10px] font-medium text-primary text-center py-1 rounded-lg bg-primary/10">
                          ✓ Current plan
                        </div>
                      ) : isUpgrade ? (
                        <button
                          type="button"
                          onClick={() => setPlanChangeTarget(p)}
                          className="mt-auto w-full text-[11px] font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg py-1.5 transition-colors"
                        >
                          Upgrade ↑
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPlanChangeTarget(p)}
                          className="mt-auto w-full text-[11px] font-semibold text-muted-foreground border border-border hover:border-primary/40 hover:text-foreground rounded-lg py-1.5 transition-colors"
                        >
                          Downgrade ↓
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* ── Plan Change Confirmation Dialog ── */}
            <Dialog open={!!planChangeTarget} onOpenChange={v => { if (!v) setPlanChangeTarget(null); }}>
              <DialogContent className="max-w-md rounded-2xl">
                {planChangeTarget && (() => {
                  const isUp = planChangeTarget.priceMonthly > realPlanDef.priceMonthly;
                  const prorated = getProratedAmount(planChangeTarget);
                  return (<>
                    <DialogHeader>
                      <DialogTitle className="text-lg flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-md ${planChangeTarget.color}`} />
                        {isUp ? "Upgrade to" : "Downgrade to"} {planChangeTarget.label}
                      </DialogTitle>
                      <DialogDescription className="text-sm text-muted-foreground">
                        Switching from <strong>{realPlanDef.label}</strong> ({realPlanDef.price}/mo)
                        {" → "}
                        <strong>{planChangeTarget.label}</strong> ({planChangeTarget.price}/mo)
                      </DialogDescription>
                    </DialogHeader>

                    <div className={`rounded-xl p-4 space-y-3 ${isUp ? "bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" : "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"}`}>
                      {isUp ? (
                        <>
                          <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-semibold text-sm">
                            <TrendingUp className="w-4 h-4" /> Upgrade — Charge today
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-muted-foreground">
                              <span>Days remaining this month</span>
                              <span className="font-mono">{daysRemaining} / {daysInMonth}</span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                              <span>Daily rate ({planChangeTarget.label})</span>
                              <span className="font-mono">${(planChangeTarget.priceMonthly / daysInMonth).toFixed(3)}</span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                              <span>Daily credit ({realPlanDef.label})</span>
                              <span className="font-mono text-red-500">−${(realPlanDef.priceMonthly / daysInMonth).toFixed(3)}</span>
                            </div>
                            <div className="border-t border-emerald-200 dark:border-emerald-700 pt-2 flex justify-between font-bold text-emerald-800 dark:text-emerald-200">
                              <span>Prorated charge today</span>
                              <span className="font-mono text-base">${prorated.toFixed(2)}</span>
                            </div>
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            From {nextRenewal} you'll be billed the full {planChangeTarget.price}/month.
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-semibold text-sm">
                            <Clock className="w-4 h-4" /> Downgrade — Effective next renewal
                          </div>
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex justify-between">
                              <span>Your current plan continues until</span>
                              <span className="font-semibold text-foreground">{nextRenewal}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>New billing from {nextRenewal}</span>
                              <span className="font-semibold text-foreground">{planChangeTarget.price}/mo</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Charge today</span>
                              <span className="font-semibold text-emerald-600">$0.00</span>
                            </div>
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            You keep full access to <strong>{realPlanDef.label}</strong> features until {nextRenewal}.
                            Some features may become unavailable after that date.
                          </p>
                        </>
                      )}
                    </div>

                    <DialogFooter className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 rounded-xl"
                        onClick={() => setPlanChangeTarget(null)}
                        disabled={planChanging}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        className={`flex-1 rounded-xl ${isUp ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                        onClick={handlePlanChange}
                        disabled={planChanging}
                      >
                        {planChanging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {isUp ? `Confirm Upgrade — $${prorated.toFixed(2)} now` : `Confirm Downgrade`}
                      </Button>
                    </DialogFooter>
                  </>);
                })()}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ── Integrations (subscribers only, not demo) ── */}
          <TabsContent value="integrations">
            {stIsDemo ? (
              <Card className="p-10 rounded-2xl border-border/50 flex flex-col items-center justify-center text-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                  <Plug className="w-7 h-7 text-muted-foreground/50" />
                </div>
                <div className="space-y-1.5 max-w-sm">
                  <h3 className="font-semibold text-base">Accounting Integrations</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect FinProject to QuickBooks, Xero, Zoho Books, and custom endpoints.
                    Available on <strong>Lite Pro</strong>, <strong>Pro</strong>, and <strong>Pro Plus</strong> subscriptions.
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/60 border border-border/60">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground font-medium">Subscribe to unlock this feature</span>
                </div>
              </Card>
            ) : (
              <IntegrationsTab companyId={companyId} isAdmin={stIsAdmin} />
            )}
          </TabsContent>
        </Tabs>
      </form>
    </div>
  );
}

// ─── DocSequencesTab ──────────────────────────────────────────────────────────

interface DocSeq {
  docType: string;
  label: string;
  mode: "auto" | "manual";
  prefix: string;
  padding: number;
  includeYear: boolean;
  yearFormat: "yyyy" | "yy";
  includeMonth: boolean;
  monthFormat: "mm" | "mmm" | "mmmm";
  includeProjectId: boolean;
  perProject: boolean;
}

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_LONG  = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildClientPreview(seq: DocSeq, counter: number): string {
  const now   = new Date();
  const parts = [seq.prefix || "?"];
  if (seq.includeYear) {
    const y = now.getFullYear();
    parts.push(seq.yearFormat === "yy" ? String(y).slice(2) : String(y));
  }
  if (seq.includeMonth) {
    const m = now.getMonth();
    if (seq.monthFormat === "mmmm") parts.push(MONTH_LONG[m]);
    else if (seq.monthFormat === "mmm") parts.push(MONTH_SHORT[m]);
    else parts.push(String(m + 1).padStart(2, "0"));
  }
  if (seq.includeProjectId && seq.perProject) parts.push("PROJ");
  const pad = Math.max(2, Math.min(8, seq.padding));
  parts.push(String(counter).padStart(pad, "0"));
  return parts.join("-");
}

/* ── DocSeqEntry: inner content (no Card wrapper) ─────────────────────────── */
function DocSeqEntry({
  seq,
  companyId,
  onUpdate,
  onSave,
  saving,
}: {
  seq: DocSeq;
  companyId: string;
  onUpdate: (patch: Partial<DocSeq>) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const [nextCounter, setNextCounter] = useState<number | null>(null);
  const [loadingRef, setLoadingRef] = useState(false);

  useEffect(() => {
    if (seq.mode !== "auto") { setNextCounter(null); return; }
    setLoadingRef(true);
    apiGet<{ refNumber: string }>(`/doc-sequences/${seq.docType}/next-ref?companyId=${companyId}`)
      .then(r => {
        const match = r.refNumber?.match(/(\d+)$/);
        setNextCounter(match ? parseInt(match[1], 10) : 1);
      })
      .catch(() => setNextCounter(null))
      .finally(() => setLoadingRef(false));
  }, [seq.mode, seq.docType, companyId]);

  const previewRef = nextCounter !== null ? buildClientPreview(seq, nextCounter) : null;
  const isAuto = seq.mode === "auto";

  return (
    <div>
      {/* Sub-header: label + badge + toggle + save */}
      <div className="flex items-center gap-3 px-5 py-3 bg-muted/20">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight">{seq.label}</p>
          <span className="inline-block mt-0.5 px-1.5 py-0 rounded text-[10px] font-mono bg-muted text-muted-foreground border border-border/60">{seq.docType}</span>
        </div>
        <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5 shrink-0">
          {(["auto", "manual"] as const).map(m => (
            <button
              key={m}
              onClick={() => onUpdate({ mode: m })}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                seq.mode === m ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m === "auto" ? "Auto" : "Manual"}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={onSave} disabled={saving} className="h-8 px-4 rounded-lg text-xs shrink-0">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
        </Button>
      </div>

      {/* Auto settings */}
      {isAuto && (
        <div className="px-5 py-4 space-y-4">
          {/* Row 1: Prefix · Digits · Scope */}
          <div className="grid grid-cols-[7rem_6rem_1fr] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Prefix</Label>
              <Input
                value={seq.prefix}
                onChange={e => onUpdate({ prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                placeholder="INV"
                className="h-8 text-sm font-mono rounded-lg"
                maxLength={8}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Digits</Label>
              <Select value={String(seq.padding)} onValueChange={v => onUpdate({ padding: parseInt(v, 10) })}>
                <SelectTrigger className="h-8 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2,3,4,5,6].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Numbering Scope</Label>
              <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5 w-fit">
                {([true, false] as const).map(perProj => (
                  <button
                    key={String(perProj)}
                    onClick={() => onUpdate({ perProject: perProj })}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                      seq.perProject === perProj ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {perProj ? "Per Project" : "Company-wide"}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug max-w-xs">
                {seq.perProject
                  ? "Counter is independent per project — each project restarts from 001."
                  : "Counter is shared across all projects — the next number continues from the last one issued company-wide."}
              </p>
            </div>
          </div>

          {/* Row 2: Year · Month · Project ID */}
          <div className="flex flex-wrap gap-2">
            <div className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 transition-colors ${seq.includeYear ? "border-primary/30 bg-primary/5" : "border-border/50 bg-muted/30"}`}>
              <Switch checked={seq.includeYear} onCheckedChange={v => onUpdate({ includeYear: v })} className="scale-90" />
              <span className="text-xs font-medium">Year</span>
              {seq.includeYear && (
                <Select value={seq.yearFormat} onValueChange={v => onUpdate({ yearFormat: v as "yyyy" | "yy" })}>
                  <SelectTrigger className="h-7 w-28 text-xs rounded-lg font-mono border-primary/20 bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yyyy">2026 (yyyy)</SelectItem>
                    <SelectItem value="yy">26 (yy)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 transition-colors ${seq.includeMonth ? "border-primary/30 bg-primary/5" : "border-border/50 bg-muted/30"}`}>
              <Switch checked={seq.includeMonth} onCheckedChange={v => onUpdate({ includeMonth: v })} className="scale-90" />
              <span className="text-xs font-medium">Month</span>
              {seq.includeMonth && (
                <Select value={seq.monthFormat} onValueChange={v => onUpdate({ monthFormat: v as "mm" | "mmm" | "mmmm" })}>
                  <SelectTrigger className="h-7 w-32 text-xs rounded-lg font-mono border-primary/20 bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mm">03 (mm)</SelectItem>
                    <SelectItem value="mmm">Mar (mmm)</SelectItem>
                    <SelectItem value="mmmm">March (mmmm)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {seq.perProject && (
              <div className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 transition-colors ${seq.includeProjectId ? "border-primary/30 bg-primary/5" : "border-border/50 bg-muted/30"}`}>
                <Switch checked={seq.includeProjectId} onCheckedChange={v => onUpdate({ includeProjectId: v })} className="scale-90" />
                <span className="text-xs font-medium">Project ID</span>
              </div>
            )}
          </div>

          {/* Live preview bar */}
          <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/15 px-4 py-2.5">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Next Reference</p>
              {loadingRef ? (
                <div className="flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Loading…</span>
                </div>
              ) : previewRef ? (
                <span className="font-mono text-base font-bold text-primary tracking-widest">{previewRef}</span>
              ) : (
                <span className="text-xs text-muted-foreground italic">Save to see preview</span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground text-right leading-relaxed">
              Assigned to the next<br />new document
            </div>
          </div>
        </div>
      )}

      {/* Manual mode notice */}
      {!isAuto && (
        <div className="px-5 py-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Reference numbers will be entered manually when creating each document of this type.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── DocSeqGroupCard: one card wrapping N DocSeqEntry sub-sections ─────────── */
function DocSeqGroupCard({
  label,
  note,
  seqs,
  columns = 1,
  companyId,
  onUpdate,
  onSave,
  saving,
}: {
  label: string;
  note?: string;
  seqs: DocSeq[];
  columns?: 1 | 2 | 3;
  companyId: string;
  onUpdate: (docType: string, patch: Partial<DocSeq>) => void;
  onSave: (docType: string) => void;
  saving: string | null;
}) {
  const colClass = columns === 3 ? "grid-cols-3" : columns === 2 ? "grid-cols-2" : "grid-cols-1";

  return (
    <Card className="rounded-2xl border-border/50 overflow-hidden">
      {/* Group header */}
      <div className="px-5 py-2.5 bg-muted/40 border-b border-border/50">
        <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
        {note && <p className="text-[10px] text-muted-foreground/70 italic mt-0.5">{note}</p>}
      </div>
      {/* Sub-sections: vertical stack (columns=1) or horizontal grid (columns>1) */}
      {columns > 1 ? (
        <div className={`grid ${colClass} divide-x divide-border/50`}>
          {seqs.map(seq => (
            <DocSeqEntry
              key={seq.docType}
              seq={seq}
              companyId={companyId}
              onUpdate={patch => onUpdate(seq.docType, patch)}
              onSave={() => onSave(seq.docType)}
              saving={saving === seq.docType}
            />
          ))}
        </div>
      ) : (
        <>
          {seqs.map((seq, i) => (
            <div key={seq.docType}>
              {i > 0 && <div className="border-t border-border/50" />}
              <DocSeqEntry
                seq={seq}
                companyId={companyId}
                onUpdate={patch => onUpdate(seq.docType, patch)}
                onSave={() => onSave(seq.docType)}
                saving={saving === seq.docType}
              />
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

function DocSequencesTab({ companyId }: { companyId: string }) {
  const { toast } = useToast();
  const [seqs, setSeqs] = useState<DocSeq[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiGet<DocSeq[]>(`/doc-sequences?companyId=${companyId}`)
      .then(data => setSeqs(data))
      .catch(() => toast({ title: "Failed to load sequences", variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [companyId]);

  const update = (docType: string, patch: Partial<DocSeq>) => {
    setSeqs(prev => prev.map(s => s.docType === docType ? { ...s, ...patch } : s));
  };

  const save = async (seq: DocSeq) => {
    setSaving(seq.docType);
    try {
      await apiPut(`/doc-sequences/${seq.docType}?companyId=${companyId}`, {
        mode:             seq.mode,
        prefix:           seq.prefix,
        padding:          seq.padding,
        includeYear:      seq.includeYear,
        yearFormat:       seq.yearFormat,
        includeMonth:     seq.includeMonth,
        monthFormat:      seq.monthFormat,
        includeProjectId: seq.includeProjectId,
        perProject:       seq.perProject,
      });
      toast({ title: "Saved", description: `${seq.label} sequence updated.` });
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  const seqMap = Object.fromEntries(seqs.map(s => [s.docType, s]));

  type GroupDef = { label: string; note?: string; types: string[]; columns?: 1 | 2 | 3; fullWidth?: boolean };
  type CategoryDef = { label: string; groups: GroupDef[] };

  const CATEGORIES: CategoryDef[] = [
    {
      label: "Clients",
      groups: [
        {
          label:     "Commercial Documents",
          note:      "QT / VO / DS share the same document table — each type has its own independent counter.",
          types:     ["quotation", "vo", "descope"],
          columns:   3,
          fullWidth: true,
        },
        {
          label:     "Invoices & Receipts",
          types:     ["client-invoice", "client-receipt"],
          columns:   2,
          fullWidth: true,
        },
      ],
    },
    {
      label: "Subcontractors",
      groups: [
        {
          label:   "Purchase Orders & VOs",
          types:   ["sc-po", "sc-vo", "sc-ds"],
          columns: 3,
        },
        {
          label:   "Invoices & Payments",
          types:   ["sc-invoice", "sc-payment"],
          columns: 2,
        },
      ],
    },
    {
      label: "Suppliers",
      groups: [
        {
          label:   "Purchase Orders & VOs",
          types:   ["sp-po", "sp-vo", "sp-ds"],
          columns: 3,
        },
        {
          label:   "Invoices & Payments",
          types:   ["supplier-invoice", "supplier-payment"],
          columns: 2,
        },
      ],
    },
    {
      label: "Payroll & Expenses",
      groups: [
        {
          label:     "Payroll & Expenses",
          types:     ["payroll", "expense"],
          columns:   2,
          fullWidth: true,
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-base">Document Reference Sequences</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Configure how reference numbers are generated for each document type. Toggle Auto to have the system assign sequential numbers, or Manual to enter them yourself.
        </p>
      </div>

      {CATEGORIES.map(cat => {
        const catGroups = cat.groups
          .map(g => ({ ...g, seqs: g.types.map(t => seqMap[t]).filter(Boolean) as DocSeq[] }))
          .filter(g => g.seqs.length > 0);
        if (catGroups.length === 0) return null;
        return (
          <div key={cat.label} className="space-y-3">
            {/* Category divider */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border/60" />
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{cat.label}</span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            {/* Cards */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {catGroups.map(group => (
                <div key={group.label} className={group.fullWidth ? "xl:col-span-2" : ""}>
                  <DocSeqGroupCard
                    label={group.label}
                    note={group.note}
                    seqs={group.seqs}
                    columns={group.columns ?? 1}
                    companyId={companyId}
                    onUpdate={(docType, patch) => update(docType, patch)}
                    onSave={docType => {
                      const seq = seqs.find(s => s.docType === docType);
                      if (seq) save(seq);
                    }}
                    saving={saving}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
